import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Alert, Badge, Button, Card, Carousel, Col, Container, Fade, Modal, Row, Spinner} from 'react-bootstrap';
import {Helmet} from 'react-helmet';
import {withTranslation} from 'react-i18next';
import {FaBuilding, FaCalendarPlus, FaTv, FaUserCheck, FaUsers} from 'react-icons/fa';
import * as api from '../api';
import {askInvoiceRequest} from '../utils/invoice-request';
import {openVideoPlayer, preconnectVideoPlayer} from '../utils/video-player-preload';
import Configuration from '../config';
import {groupCatalogItemsByTiming} from '../util/catalog-timing';
import {ROLES, subscriptionKind, subscriptionStatuses} from '../constants';
import {
  addVideoToPlayerArea,
  filterBookableFields,
  filterBookingFieldId,
  getPartnerFields,
  getPartnerServices,
  getPlayerAffiliations,
  getRtvVideos,
  getSlots,
  partnerSelected,
  permanentVideoSelected,
  refreshPlayerProfile,
  rtvChannelSelected,
  specialEventSelected,
  showVideoHighlightModal
} from '../actions/player';
import {logout} from '../actions/login';
import {
  getOwnedFields,
  getOwnedFieldBookings,
  getOwnedFieldSlots,
  ownedFieldSelected,
  partnerFieldsActiveTab
} from '../actions/partner';
import facebookIcon from '../images/facebook_pos_icon.svg';
import instagramIcon from '../images/instagram_pos_icon.svg';
import youtubeIcon from '../images/youtube_pos_icon.svg';
import parkingIcon from '../images/parking_icon.svg';
import lockerRoomsIcon from '../images/locker_room_icon.svg';
import showersIcon from '../images/shower_icon.svg';
import restaurantIcon from '../images/ristorante.svg';
import barIcon from '../images/bar_icon.svg';
import shopIcon from '../images/negozio.svg';
import picnicIcon from '../images/picnic_area_icon.svg';
import tournamentsIcon from '../images/tournaments_icon.svg';
import petFriendlyIcon from '../images/pets_icon.svg';
import accessibilityIcon from '../images/disabled_icon.svg';
import relaxAreaIcon from '../images/relax_icon.svg';
import checkIcon from '../images/check_icon.svg';
import clockIcon from '../images/clock_icon.svg';
import onOffIcon from '../images/on-off_icon.svg';
import replayerTvIcon from '../images/replayer_tv_icon.svg';
import PlayerHighLight from './player-highlight';
import PlayerBookingCalendar from '../containers/player-booking-calendar';
import PublicPartnerBookingCalendar from './public-partner-booking-calendar';
import PublicPartnerSkyworkActionRibbon from './public-partner/public-partner-skywork-action-ribbon';
import PublicPartnerSkyworkAffiliations from './public-partner/public-partner-skywork-affiliations';
import {
  createAffiliationCommercialDetails,
  createAffiliationRequestState
} from './public-partner/public-partner-affiliation-view-model';
import {createPublicPartnerSkyworkViewModel} from './public-partner/public-partner-skywork-adapter';
import PublicPartnerSkyworkHero from './public-partner/public-partner-skywork-hero';
import PublicPartnerSkyworkTvSection from './public-partner/public-partner-skywork-tv-section';
import PublicPartnerSkyworkVideoCard from './public-partner/public-partner-skywork-video-card';
import {
  hasActiveSubscriptionForCatalogItem,
  resolvePublicPartnerVideoCardState
} from './public-partner/public-partner-video-card-logic';
import {
  createPublicSpecialEventOffer,
  createPublicSpecialEventPaywallPresentation,
  createPublicSpecialEventPresentation
} from './public-partner/public-partner-special-event-offer';
import {
  PublicPartnerSkyworkEvents,
  PublicPartnerSkyworkPlanner,
  PublicPartnerSkyworkShop,
  SectionHeading
} from './public-partner/public-partner-skywork-journey-sections';
import {getVideoFieldSport, isPickleballVideo} from '../util/video-sports';
import {specialEventHomeCardContext} from './special-event-home-card';
import {
  CLUB_BOOKING_SAVED_EVENT,
  CLUB_MODE_CHANGED_EVENT,
  CLUB_PENDING_ACTION_KEY,
  CLUB_PENDING_BOOKING_SLOT_KEY,
  CLUB_RETURN_PATH_KEY,
  activateClubMode,
  clearClubMode,
  getActiveClubPath,
  navigateToAuthenticatedPlayerFlow
} from '../util/club-mode';
import '../css/public-partner-skywork-page.css';
import {isPartnerManagingCatalog} from '../util/partner-club-access';
import {
  catalogItemNeedsThumbnailRefresh,
  mergeCatalogThumbnailUpdates
} from '../util/public-catalog-thumbnails';

const CONTENT_TYPE_LABEL = {
  rtv_slot: 'PUBLIC_CATALOG_CONTENT_REPLAYERTV',
  store_slot: 'PUBLIC_CATALOG_CONTENT_VIDEO_STORE',
  academy_slot: 'PUBLIC_CATALOG_CONTENT_COACH',
  special_event_slot: 'PUBLIC_CATALOG_CONTENT_EVENT',
  partner_video: 'PUBLIC_CATALOG_CONTENT_REPLAYERTV'
};

const UPCOMING_PAGE_SIZE = 15;
const THUMBNAIL_REFRESH_INTERVAL_MS = 15000;

export const createClubSectionNumbers = ({
  hasEvents,
  hasOpenMatches,
  hasPlanner,
  hasShop,
  showAffiliations
}) => {
  let current = 1;
  const take = visible => visible ? String(current++).padStart(2, '0') : null;
  return {
    events: take(hasEvents),
    planner: take(hasPlanner),
    openMatches: take(hasOpenMatches),
    tv: take(true),
    affiliations: take(showAffiliations),
    shop: take(hasShop)
  };
};

const PAYWALL_MESSAGE = {
  no_subscription: 'PUBLIC_CATALOG_PAYWALL_SUBSCRIPTION',
  no_purchase: 'PUBLIC_CATALOG_PAYWALL_PURCHASE',
  affiliation_required: 'PUBLIC_CATALOG_PAYWALL_AFFILIATION',
  private_whitelist_required: 'PUBLIC_CATALOG_PAYWALL_PRIVATE'
};

const AFFILIATION_STATUS_TRANSLATION_KEY = {
  available: 'CLUB_SKYWORK_AFFILIATION_STATUS_AVAILABLE',
  pending: 'CLUB_SKYWORK_AFFILIATION_STATUS_PENDING',
  active: 'CLUB_SKYWORK_AFFILIATION_STATUS_ACTIVE',
  rejected: 'CLUB_SKYWORK_AFFILIATION_STATUS_REJECTED',
  expired: 'CLUB_SKYWORK_AFFILIATION_STATUS_EXPIRED',
  renewal_pending: 'CLUB_SKYWORK_AFFILIATION_STATUS_RENEWAL_PENDING',
  expired_renewal_pending: 'CLUB_SKYWORK_AFFILIATION_STATUS_EXPIRED_RENEWAL_PENDING',
  inactive: 'CLUB_SKYWORK_AFFILIATION_STATUS_INACTIVE',
  unavailable: 'CLUB_SKYWORK_AFFILIATION_STATUS_UNAVAILABLE'
};

const AFFILIATION_STATUS_SHORT_TRANSLATION_KEY = {
  available: 'CLUB_SKYWORK_AFFILIATION_BADGE_AVAILABLE',
  pending: 'CLUB_SKYWORK_AFFILIATION_BADGE_PENDING',
  active: 'CLUB_SKYWORK_AFFILIATION_BADGE_ACTIVE',
  rejected: 'CLUB_SKYWORK_AFFILIATION_BADGE_REJECTED',
  expired: 'CLUB_SKYWORK_AFFILIATION_BADGE_EXPIRED',
  renewal_pending: 'CLUB_SKYWORK_AFFILIATION_BADGE_RENEWAL_PENDING',
  expired_renewal_pending: 'CLUB_SKYWORK_AFFILIATION_BADGE_RENEWAL_PENDING',
  inactive: 'CLUB_SKYWORK_AFFILIATION_BADGE_INACTIVE',
  unavailable: 'CLUB_SKYWORK_AFFILIATION_BADGE_UNAVAILABLE'
};

const FACILITY_LABELS = {
  parking: 'FACILITY_PARKING',
  lockerRooms: 'FACILITY_LOCKER_ROOMS',
  showers: 'FACILITY_SHOWERS',
  restaurant: 'FACILITY_RESTAURANT',
  bar: 'FACILITY_BAR',
  shop: 'FACILITY_SHOP',
  picnic: 'FACILITY_PICNIC',
  tournaments: 'FACILITY_TOURNAMENTS',
  petFriendly: 'FACILITY_PET_FRIENDLY',
  accessibility: 'FACILITY_ACCESSIBILITY',
  relaxArea: 'FACILITY_RELAX_AREA'
};

const FACILITY_ICONS = {
  parking: parkingIcon,
  lockerRooms: lockerRoomsIcon,
  showers: showersIcon,
  restaurant: restaurantIcon,
  bar: barIcon,
  shop: shopIcon,
  picnic: picnicIcon,
  tournaments: tournamentsIcon,
  petFriendly: petFriendlyIcon,
  accessibility: accessibilityIcon,
  relaxArea: relaxAreaIcon
};

const getAffiliationProfileName = affiliation => {
  if (!affiliation) return '';
  if (affiliation.type === 'coach') return 'Coach';
  if (affiliation.type === 'rtv') return 'ReplayerTV';
  return affiliation.name;
};

const getAffiliationIcon = affiliation => {
  if (affiliation.status === 'active') return checkIcon;
  if (affiliation.status === 'pending' || affiliation.renewalPending) return clockIcon;
  if (affiliation.type === 'rtv') return replayerTvIcon;
  return onOffIcon;
};

const SUBSCRIPTION_KIND_LABEL = {
  rtv_dedicated: 'PUBLIC_CATALOG_SUBSCRIPTION_DEDICATED',
  rtv_special_event: 'PUBLIC_CATALOG_SUBSCRIPTION_EVENT',
  rtv_unlimited: 'PUBLIC_CATALOG_SUBSCRIPTION_UNLIMITED'
};

const VIDEO_CARD_LABEL_KEYS = {
  'In ritardo': 'PUBLIC_CATALOG_LIVE_DELAYED',
  'Video programmato': 'VIDEO_SCHEDULED',
  'In attesa di upload': 'PUBLIC_CATALOG_WAITING_UPLOAD',
  'Upload in corso': 'PUBLIC_CATALOG_UPLOAD_IN_PROGRESS',
  'Video in elaborazione': 'PUBLIC_CATALOG_VIDEO_PROCESSING',
  'Video pronto': 'PUBLIC_CATALOG_VIDEO_READY',
  'Video non disponibile': 'PUBLIC_CATALOG_VIDEO_UNAVAILABLE',
  'Verifica in corso...': 'PUBLIC_CATALOG_VERIFYING',
  'Guarda la partita': 'PUBLIC_CATALOG_WATCH_MATCH',
  'Verifica accesso...': 'PUBLIC_CATALOG_ACCESS_CHECKING_SHORT',
  'Scopri le opzioni di visione': 'PUBLIC_CATALOG_DISCOVER_VIEWING_OPTIONS',
  'Verifica accesso': 'PUBLIC_CATALOG_CHECK_ACCESS'
};

const isFreeCatalogItem = item => {
  if (!item || item.contentType === 'academy_slot') return false;
  if (item.lockHint === 'none') return true;

  const commerceFlags = item.commerceFlags || {};
  const hasFreeSinglePurchase = commerceFlags.singlePurchaseAvailable
    && Number(commerceFlags.singlePurchasePriceEur) === 0;
  const hasFreeClubSubscription = (commerceFlags.subscriptionOffers || []).some(offer => (
    ['rtv_dedicated', 'rtv_special_event'].includes(offer.kind)
      && Number(offer.priceEur) === 0
  ));

  return hasFreeSinglePurchase || hasFreeClubSubscription;
};

const groupStoreCatalogItemsByTiming = items => {
  const groupedItems = groupCatalogItemsByTiming(items);
  groupedItems.replay.sort((a, b) => new Date(a.startDatetime || 0) - new Date(b.startDatetime || 0));
  return groupedItems;
};

const catalogItemKey = item => `${item.contentType}:${item.contentId}`;

const subscriptionOfferKey = offer => [
  (offer && offer.kind) || '',
  (offer && offer.planId) || '',
  (offer && offer.partnerId) || '',
  (offer && offer.channelId) || '',
  (offer && offer.eventId) || ''
].join(':');

const mergeSubscriptionOffers = (...offerGroups) => {
  const offers = [];
  const offerIndexes = {};

  offerGroups.forEach(group => {
    (group || []).filter(Boolean).forEach(offer => {
      const key = subscriptionOfferKey(offer);
      if (offerIndexes[key] === undefined) {
        offerIndexes[key] = offers.length;
        offers.push(offer);
        return;
      }

      offers[offerIndexes[key]] = {
        ...offers[offerIndexes[key]],
        ...offer
      };
    });
  });

  return offers;
};

const mergeCommerceFlags = (existingFlags = {}, itemFlags = {}) => {
  const currentFlags = existingFlags || {};
  const nextFlags = itemFlags || {};

  return {
    ...currentFlags,
    ...nextFlags,
    subscriptionOffers: mergeSubscriptionOffers(
      currentFlags.subscriptionOffers,
      nextFlags.subscriptionOffers
    ),
    singlePurchaseAvailable: Boolean(currentFlags.singlePurchaseAvailable || nextFlags.singlePurchaseAvailable),
    singlePurchasePriceEur: nextFlags.singlePurchasePriceEur !== undefined
      ? nextFlags.singlePurchasePriceEur
      : currentFlags.singlePurchasePriceEur
  };
};

const mergeCatalogItems = (...itemGroups) => {
  const mergedItems = [];
  const itemIndexes = {};

  itemGroups.forEach(items => {
    (items || []).forEach(item => {
      const key = catalogItemKey(item);
      if (itemIndexes[key] === undefined) {
        itemIndexes[key] = mergedItems.length;
        mergedItems.push(item);
        return;
      }
      const existing = mergedItems[itemIndexes[key]];
      mergedItems[itemIndexes[key]] = {
        ...existing,
        ...item,
        commerceFlags: mergeCommerceFlags(existing.commerceFlags, item.commerceFlags)
      };
    });
  });

  return mergedItems;
};

const getPartnerRtvChannels = partner => (
  partner
    ? partner.rtvChannels || partner.rtv_channels || []
    : []
);

const isSafePath = path => typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

const formatDateTime = (isoDate, locale, unavailableLabel) => {
  if (!isoDate) {
    return unavailableLabel;
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return unavailableLabel;
  }

  return date.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatOpenMatchDate = (isoDate, locale, unavailableLabel) => {
  if (!isoDate) {
    return unavailableLabel;
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return unavailableLabel;
  }

  return date.toLocaleDateString(locale, {
    weekday: 'short',
    day: '2-digit',
    month: 'short'
  });
};

const partnerInitials = name => String(name || 'R')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0).toUpperCase())
  .join('');

const formatEur = (value, locale) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return '-';
  }

  return numeric.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
};

const formatVideoDuration = seconds => {
  const numeric = Number(seconds);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }

  return new Date(numeric * 1000).toISOString().slice(11, 19);
};

const formatVideoFileSize = bytes => {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }

  if (numeric >= 1000000000) {
    return `${(numeric / 1000000000).toFixed(2)} GB`;
  }

  return `${(numeric / 1000000).toFixed(0)} MB`;
};

const normalizeDisplayText = value => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim();
};

const sameDisplayText = (a, b) => (
  normalizeDisplayText(a).toLowerCase() === normalizeDisplayText(b).toLowerCase()
);

const getCatalogItemTitle = (item, fallbackTitle) => (
  normalizeDisplayText(item.title)
    || normalizeDisplayText(item.name)
    || normalizeDisplayText(item.fieldOrServiceName)
    || fallbackTitle
);

const getCatalogItemDescription = (item, title, fallbackDescription) => {
  const candidates = [
    item.description,
    item.slotDescription,
    item.bookingDescription,
    item.matchDescription,
    item.videoDescription,
    item.subtitle
  ];
  const description = candidates
    .map(normalizeDisplayText)
    .find(value => value && !sameDisplayText(value, title));

  return description || fallbackDescription;
};

const getCatalogItemVenueLabel = item => (
  normalizeDisplayText(item.fieldOrServiceName)
  || normalizeDisplayText(item.fieldName)
  || normalizeDisplayText(item.serviceName)
  || ''
);

const getCatalogItemDuration = item => formatVideoDuration(
  item.durationSeconds
  || item.duration
  || item.videoDurationSeconds
  || item.videoDuration
);

const getCatalogItemFileSize = item => formatVideoFileSize(
  item.filesize
  || item.fileSize
  || item.videoFileSize
  || item.videoFileSizeBytes
  || item.sizeBytes
);

const getCatalogItemThumbnailUrl = item => {
  const candidate = item && (
    item.thumbnailUrl
    || item.thumbnail_url
    || item.previewImageUrl
    || item.preview_image_url
    || item.posterUrl
    || item.poster_url
  );
  return typeof candidate === 'string' && /^(https?:|data:|blob:)/i.test(candidate)
    ? candidate
    : null;
};

const getCatalogItemThumbnailFallbackUrl = item => {
  const candidate = item && (
    item.thumbnailFallbackUrl
    || item.thumbnail_fallback_url
  );
  return typeof candidate === 'string' && /^(https?:|data:|blob:)/i.test(candidate)
    ? candidate
    : null;
};

const buildPlayerUrl = playback => {
  const params = new URLSearchParams();
  if (playback.slotId) {
    params.set('slotId', String(playback.slotId));
  }
  if (playback.videoPartnerId) {
    params.set('videoPartnerId', String(playback.videoPartnerId));
  }
  params.set('token', playback.krpanoToken);
  return `${Configuration.krpanoPlayerUrl}?${params.toString()}`;
};

const PublicPartnerCatalog = ({dispatch, history, match, location, profile, partners, selectedPermanentVideo, subscriptions, t, token: authToken}) => {
  const locale = t('LOCALE_CODE');
  const partnerSlug = match.params.partnerSlug;
  const queryParams = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const isTvView = queryParams.get('view') === 'tv';
  const selectedRtvChannelId = queryParams.get('channel_id');
  const token = profile && profile.user && [ROLES.partner, ROLES.admin].includes(profile.user.role)
    ? null
    : authToken;
  const [catalog, setCatalog] = useState({partner: null, items: [], specialEvents: []});
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [loadingContentAccess, setLoadingContentAccess] = useState(false);
  const [playActionLoadingId, setPlayActionLoadingId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [paywallOffer, setPaywallOffer] = useState(null);
  const [authenticationRequired, setAuthenticationRequired] = useState(null);
  const [videoUnavailableNotice, setVideoUnavailableNotice] = useState(null);
  const [affiliations, setAffiliations] = useState([]);
  const [loadingAffiliations, setLoadingAffiliations] = useState(true);
  const [requestingAffiliationId, setRequestingAffiliationId] = useState(null);
  const [affiliationToConfirm, setAffiliationToConfirm] = useState(null);
  const [affiliationRequestError, setAffiliationRequestError] = useState(null);
  const [purchaseActionId, setPurchaseActionId] = useState(null);
  const [videoReadyNotificationLoadingId, setVideoReadyNotificationLoadingId] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);
  const [showClubShowcase, setShowClubShowcase] = useState(false);
  const [showBookingCalendar, setShowBookingCalendar] = useState(false);
  const [showBookingDestinationChoice, setShowBookingDestinationChoice] = useState(false);
  const [pendingBookingSlotId, setPendingBookingSlotId] = useState(null);
  const [bookingAccountConfirmed, setBookingAccountConfirmed] = useState(false);
  const [authenticatedClubPath, setAuthenticatedClubPath] = useState(
    getActiveClubPath()
  );
  const [contentAccess, setContentAccess] = useState({});
  const [matchListMode, setMatchListMode] = useState('replay');
  const [upcomingVisibleLimit, setUpcomingVisibleLimit] = useState(UPCOMING_PAGE_SIZE);
  const [upcomingPagination, setUpcomingPagination] = useState({
    publicCursor: null,
    hasMore: false
  });
  const [loadingMoreUpcoming, setLoadingMoreUpcoming] = useState(false);
  const [replayPagination, setReplayPagination] = useState({
    publicCursor: null,
    hasMore: false
  });
  const [loadingMoreReplay, setLoadingMoreReplay] = useState(false);
  const [storeCatalogLoaded, setStoreCatalogLoaded] = useState(false);
  const [loadingStoreCatalog, setLoadingStoreCatalog] = useState(false);
  const [clubOpenMatches, setClubOpenMatches] = useState([]);
  const [loadingClubOpenMatches, setLoadingClubOpenMatches] = useState(false);
  const catalogHandlers = useRef({});
  const catalogRequestId = useRef(0);
  const thumbnailRefreshInFlight = useRef(false);
  const isManagingOwnClub = isPartnerManagingCatalog(profile, catalog.partner);

  const openPartnerCustomerBooking = () => {
    dispatch(getOwnedFields());
    dispatch(getOwnedFieldBookings(new Date(), 'WEEK'));
    dispatch(getOwnedFieldSlots(null, true, 'field', new Date(), 'WEEK'));
    dispatch(ownedFieldSelected(null));
    dispatch(partnerFieldsActiveTab('campi'));
    history.push('/partner-booking-calendar');
  };

  useEffect(() => {
    if (paywallOffer) {
      setShowTopUp(false);
    }
  }, [paywallOffer]);

  const safeNextPath = useMemo(() => (
    isTvView
      ? `/club/${partnerSlug}?view=tv${selectedRtvChannelId ? `&channel_id=${selectedRtvChannelId}` : ''}`
      : `/club/${partnerSlug}`
  ), [isTvView, partnerSlug, selectedRtvChannelId]);
  const authenticatedClubPage = Boolean(token && (isTvView || authenticatedClubPath === safeNextPath));
  const coachAffiliation = affiliations.find(affiliation => affiliation.type === 'coach');
  const clubLogoUrl = catalog.partner && catalog.partner.logoUrl
    ? `${Configuration.apiEndPoint}/images/${catalog.partner.id}?load=${encodeURIComponent(catalog.partner.logoUrl)}`
    : null;

  useEffect(() => {
    if (
      token &&
      profile &&
      profile.user &&
      profile.user.role === ROLES.player &&
      authenticatedClubPath !== safeNextPath
    ) {
      activateClubMode(safeNextPath);
      setAuthenticatedClubPath(safeNextPath);
    }
  }, [authenticatedClubPath, profile, safeNextPath, token]);

  const rtvCatalogItems = useMemo(() => (
    catalog.items.filter(item => item.contentType !== 'store_slot')
  ), [catalog.items]);
  const storeCatalogItems = useMemo(() => (
    catalog.items.filter(item => item.contentType === 'store_slot')
  ), [catalog.items]);
  const groupedCatalogItems = useMemo(() => (
    groupCatalogItemsByTiming(rtvCatalogItems)
  ), [rtvCatalogItems]);
  const visibleUpcomingItems = useMemo(() => (
    groupedCatalogItems.upcoming.slice(0, upcomingVisibleLimit)
  ), [groupedCatalogItems.upcoming, upcomingVisibleLimit]);
  const canLoadMoreUpcoming = visibleUpcomingItems.length < groupedCatalogItems.upcoming.length
    || upcomingPagination.hasMore;
  const groupedStoreCatalogItems = useMemo(() => (
    groupStoreCatalogItemsByTiming(storeCatalogItems)
  ), [storeCatalogItems]);
  const selectedMatchListMode = matchListMode === 'replay'
    && groupedCatalogItems.replay.length === 0
    && groupedCatalogItems.upcoming.length > 0
    ? 'upcoming'
    : matchListMode;
  const rtvChannels = useMemo(() => {
    if (!catalog.partner) {
      return [];
    }

    return getPartnerRtvChannels(catalog.partner);
  }, [catalog.partner]);
  const showRtvChannelFilter = rtvChannels.length > 1;
  const subscriptionOptionsItem = useMemo(() => (
    catalog.items.find(item => (
      item.commerceFlags
      && item.commerceFlags.subscriptionOffers
      && item.commerceFlags.subscriptionOffers.length > 0
    ))
  ), [catalog.items]);

  const showSubscriptionOptionsButton = Boolean(
    subscriptionOptionsItem
  );
  const catalogHasPendingThumbnails = useMemo(() => (
    catalog.items.some(catalogItemNeedsThumbnailRefresh)
  ), [catalog.items]);

  useEffect(() => {
    catalogHandlers.current.rememberClubPath();
    catalogHandlers.current.loadCatalog();
  }, [partnerSlug, isTvView, selectedRtvChannelId, token]);

  useEffect(() => {
    if (!catalogHasPendingThumbnails) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        catalogHandlers.current.refreshCatalogThumbnails();
      }
    }, THUMBNAIL_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [catalogHasPendingThumbnails, partnerSlug, isTvView, selectedRtvChannelId, token]);

  useEffect(() => {
    catalogHandlers.current.loadAffiliations();
  }, [partnerSlug, token]);

  useEffect(() => {
    const refreshClubAuth = () => setAuthenticatedClubPath(
      getActiveClubPath()
    );
    window.addEventListener(CLUB_MODE_CHANGED_EVENT, refreshClubAuth);
    return () => window.removeEventListener(CLUB_MODE_CHANGED_EVENT, refreshClubAuth);
  }, []);

  useEffect(() => {
    const showDestinationChoice = () => setShowBookingDestinationChoice(true);
    window.addEventListener(CLUB_BOOKING_SAVED_EVENT, showDestinationChoice);
    return () => window.removeEventListener(CLUB_BOOKING_SAVED_EVENT, showDestinationChoice);
  }, []);

  useEffect(() => {
    if (!token || (!authenticatedClubPage && !isTvView)) {
      setContentAccess({});
    }
  }, [authenticatedClubPage, isTvView, token]);

  useEffect(() => {
    if (!token) return;
    const rawPendingAction = window.sessionStorage.getItem(CLUB_PENDING_ACTION_KEY);
    if (!rawPendingAction) return;

    let pendingAction;
    try {
      pendingAction = JSON.parse(rawPendingAction);
    } catch (error) {
      return;
    }
    if (!pendingAction || pendingAction.partnerSlug !== partnerSlug) return;
    const catalogRequiredActions = [
      'book_field',
      'single_purchase',
      'subscription',
      'verify_access',
      'video_ready_notification'
    ];
    if (
      catalogRequiredActions.includes(pendingAction.type)
      && (loadingCatalog || !catalog.partner)
    ) {
      return;
    }

    catalogHandlers.current.activateAuthenticatedClubPage();

    const resumePendingAction = async () => {
      if (pendingAction.type === 'book_field') {
        window.sessionStorage.setItem(CLUB_PENDING_BOOKING_SLOT_KEY, String(pendingAction.slotId));
        setPendingBookingSlotId(String(pendingAction.slotId));
        catalogHandlers.current.openBookingCalendar(new Date(pendingAction.startDatetime || Date.now()), true);
        return;
      }

      if (pendingAction.type === 'verify_access') {
        const item = catalog.items.find(catalogItem => (
          catalogItem.contentId === pendingAction.contentId
          && catalogItem.contentType === pendingAction.contentType
        ));
        if (item) {
          await catalogHandlers.current.loadContentAccess(item);
        }
        setNotice({
          variant: 'success',
          message: t('PUBLIC_CATALOG_PROFILE_CONTENT_REFRESHED')
        });
        return;
      }

      if (pendingAction.type === 'video_ready_notification') {
        const item = catalog.items.find(catalogItem => (
          catalogItem.contentId === pendingAction.contentId
          && catalogItem.contentType === pendingAction.contentType
        ));
        if (item) {
          await catalogHandlers.current.subscribeVideoReadyNotification(item);
        }
        return;
      }

      setPaywallOffer({
        action: 'paywall',
        contentId: pendingAction.contentId,
        contentType: pendingAction.contentType,
        reasonCode: 'subscription_options',
        managementMode: false,
        offers: {
          subscriptions: pendingAction.offer ? [pendingAction.offer] : [],
          singlePurchase: pendingAction.priceEur !== undefined
            ? {available: true, priceEur: pendingAction.priceEur}
            : null
        },
        requiredAffiliation: null
      });
      setNotice({
        variant: 'info',
        message: t('PUBLIC_CATALOG_LOGIN_RESUMED')
      });
    };

    resumePendingAction().then(() => {
      window.sessionStorage.removeItem(CLUB_PENDING_ACTION_KEY);
    });
  }, [token, partnerSlug, catalog.partner, catalog.items, loadingCatalog, t]);

  useEffect(() => {
    if (!location.hash || loadingCatalog) return;
    const section = document.getElementById(location.hash.replace(/^#/, ''));
    if (section) {
      section.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }, [location.hash, loadingCatalog, groupedCatalogItems.live.length]);

  useEffect(() => {
    const query = new URLSearchParams(location.search || '');
    const resumeToken = query.get('resume');

    if (!resumeToken || !token) {
      return;
    }

    const resumePlayIntent = async () => {
      setNotice(null);
      const result = await api.resumePublicPlayIntent(resumeToken, token);
      catalogHandlers.current.handlePlayIntentResult(result);
      const cleanPath = safeNextPath;
      window.history.replaceState({}, '', cleanPath);
    };

    resumePlayIntent();
  }, [location.search, safeNextPath, token, partnerSlug]);

  const activateAuthenticatedClubPage = () => {
    activateClubMode(safeNextPath);
    setAuthenticatedClubPath(safeNextPath);
  };

  const rememberClubPath = () => {
    window.sessionStorage.setItem(CLUB_RETURN_PATH_KEY, safeNextPath);
  };

  const scrollToVideoStore = () => {
    const section = document.getElementById('club-video-store');
    if (section) {
      section.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  };

  const loadStoreCatalog = async ({scroll = false} = {}) => {
    if (storeCatalogLoaded || loadingStoreCatalog) {
      if (scroll) {
        setTimeout(scrollToVideoStore, 0);
      }
      return;
    }

    setLoadingStoreCatalog(true);
    const requestId = catalogRequestId.current;
    const nowIso = new Date().toISOString();
    const publicCatalogBaseQuery = isTvView ? {view: 'tv'} : {};
    const [storeReplayRes, storeUpcomingRes] = await Promise.all([
      api.getPublicPartnerCatalog(partnerSlug, {
        ...publicCatalogBaseQuery,
        types: 'store',
        to: nowIso,
        limit: UPCOMING_PAGE_SIZE
      }, token),
      api.getPublicPartnerCatalog(partnerSlug, {
        ...publicCatalogBaseQuery,
        types: 'store',
        from: nowIso,
        limit: UPCOMING_PAGE_SIZE
      }, token)
    ]);

    if (requestId !== catalogRequestId.current) {
      return;
    }

    const publicStoreItems = mergeCatalogItems(
      storeReplayRes.status === 200 ? storeReplayRes.json.items : [],
      storeUpcomingRes.status === 200 ? storeUpcomingRes.json.items : []
    );

    setCatalog(currentCatalog => ({
      ...currentCatalog,
      items: mergeCatalogItems(currentCatalog.items, publicStoreItems)
    }));
    setStoreCatalogLoaded(true);
    setLoadingStoreCatalog(false);

    if (scroll) {
      setTimeout(scrollToVideoStore, 0);
    }
  };

  const openVideoStore = () => {
    loadStoreCatalog({scroll: true});
  };

  const navigateFromClub = path => {
    rememberClubPath();
    history.push(path);
  };

  const getBookingPartner = () => ({
    uid: catalog.partner.uid || catalog.partner.id,
    name: catalog.partner.name,
    phoneNumber: catalog.partner.phoneNumber,
    isSpecialist: false,
    bookableFields: Boolean(catalog.partner.bookableFields)
  });

  const selectBookingPlanner = (filterBookable = true) => {
    if (!catalog.partner) return null;
    const bookingPartner = getBookingPartner();
    dispatch(partnerSelected(bookingPartner));
    dispatch(filterBookingFieldId(null));
    dispatch(filterBookableFields(filterBookable));
    return bookingPartner;
  };

  const loadBookingPlanner = (bookingPartner, targetDate = new Date()) => {
    if (!bookingPartner) return;
    dispatch(getPartnerFields(bookingPartner.uid));
    dispatch(getPartnerServices(bookingPartner.uid));
    dispatch(getSlots(bookingPartner.uid, null, targetDate, 'field', 'DAY'));
  };

  const prepareBookingPlanner = (targetDate = new Date(), filterBookable = true) => {
    const bookingPartner = selectBookingPlanner(filterBookable);
    loadBookingPlanner(bookingPartner, targetDate);
  };

  const navigateToPlayerFlow = (path, onAuthenticated) => (
    navigateToAuthenticatedPlayerFlow({
      navigate: history.push,
      onAuthenticated,
      path,
      remember: rememberClubPath,
      token
    })
  );

  const openClubQuickBooking = () => {
    const partnerId = catalog.partner.uid || catalog.partner.id;
    navigateToPlayerFlow(`/player-show-sport-centers?bookable=true&partnerId=${encodeURIComponent(partnerId)}`);
  };

  const openClubPlanner = () => {
    const filterBookable = Boolean(catalog.partner.bookableFields);
    const path = filterBookable ? '/player-booking-calendar' : '/player-calendars';
    const targetDate = new Date();
    const bookingPartner = selectBookingPlanner(filterBookable);
    navigateToPlayerFlow(
      path,
      () => loadBookingPlanner(bookingPartner, targetDate)
    );
  };

  const selectRtvChannel = channelId => {
    const query = new URLSearchParams(location.search || '');
    query.set('view', 'tv');
    if (channelId) {
      query.set('channel_id', String(channelId));
    } else {
      query.delete('channel_id');
    }
    history.push(`/club/${partnerSlug}?${query.toString()}`);
  };

  const openBookingCalendar = async (targetDate = new Date(), accountConfirmed = false) => {
    if (!catalog.partner) return;
    setShowBookingCalendar(true);
    if (!token) {
      setBookingAccountConfirmed(false);
      return;
    }

    if (accountConfirmed && profile && profile.user && profile.user.role === ROLES.player) {
      await dispatch(getPlayerAffiliations());
    }

    prepareBookingPlanner(targetDate, true);
    setBookingAccountConfirmed(accountConfirmed);
  };

  const requestBookingLogin = slot => {
    window.sessionStorage.setItem(CLUB_PENDING_ACTION_KEY, JSON.stringify({
      partnerSlug,
      slotId: slot.slotId,
      startDatetime: slot.startDatetime,
      type: 'book_field'
    }));
    if (token) {
      clearClubMode();
      dispatch(logout());
    }
    redirectToLoginWithResume();
  };

  const useAuthenticatedPlayerForBooking = slot => {
    activateAuthenticatedClubPage();
    setPendingBookingSlotId(String(slot.slotId));
    window.sessionStorage.removeItem(CLUB_PENDING_ACTION_KEY);
    openBookingCalendar(new Date(slot.startDatetime), true);
  };

  const openCalendarVideoSlot = async slot => {
    if (!slot || !slot.slotId) return;
    setNotice(null);
    setShowTopUp(false);
    const item = {
      contentId: slot.slotId,
      contentType: 'rtv_slot',
      startDatetime: slot.startDatetime,
      endDatetime: slot.endDatetime,
      videoComplete: slot.videoReady,
      __catalogSection: 'replay'
    };
    if (token) {
      preconnectVideoPlayer();
      const pendingPlayerWindow = window.open('', '_blank');
      if (pendingPlayerWindow) {
        pendingPlayerWindow.opener = null;
      }
      const result = await api.postPublicPlayIntent({
        contentId: item.contentId,
        contentType: item.contentType
      }, token);
      handlePlayIntentResult(result, pendingPlayerWindow, item);
      return;
    }
    setPaywallOffer({
      action: 'paywall',
      contentId: item.contentId,
      contentType: item.contentType,
      reasonCode: 'no_subscription',
      offers: {
        subscriptions: [],
        singlePurchase: slot.videoPrice ? {available: true, priceEur: slot.videoPrice} : null
      }
    });
  };

  const refreshCatalogThumbnails = async () => {
    if (
      thumbnailRefreshInFlight.current
      || loadingCatalog
      || loadingMoreReplay
      || loadingMoreUpcoming
    ) {
      return;
    }
    thumbnailRefreshInFlight.current = true;
    const requestId = catalogRequestId.current;
    const nowIso = new Date().toISOString();
    const selectedChannelQuery = selectedRtvChannelId ? {channelId: selectedRtvChannelId} : {};
    const publicCatalogBaseQuery = isTvView ? {view: 'tv'} : {};

    try {
      const [mainRes, replayRes] = await Promise.all([
        api.getPublicPartnerCatalog(partnerSlug, {
          ...publicCatalogBaseQuery,
          ...selectedChannelQuery,
          types: 'rtv,events',
          from: nowIso,
          limit: UPCOMING_PAGE_SIZE
        }, token),
        api.getPublicPartnerCatalog(partnerSlug, {
          ...publicCatalogBaseQuery,
          ...selectedChannelQuery,
          types: 'rtv,events',
          to: nowIso,
          limit: UPCOMING_PAGE_SIZE
        }, token)
      ]);

      if (
        requestId !== catalogRequestId.current
        || mainRes.status !== 200
        || replayRes.status !== 200
      ) {
        return;
      }

      const refreshedItems = mergeCatalogItems(
        mainRes.json.items,
        replayRes.json.items
      );
      setCatalog(currentCatalog => ({
        ...currentCatalog,
        items: mergeCatalogThumbnailUpdates(currentCatalog.items, refreshedItems, Boolean(token))
      }));
    } catch (_error) {
      // The current cover remains valid while a background refresh is unavailable.
    } finally {
      thumbnailRefreshInFlight.current = false;
    }
  };

  const loadCatalog = async () => {
    const requestId = catalogRequestId.current + 1;
    catalogRequestId.current = requestId;
    setLoadingCatalog(true);
    setCatalogError(null);
    setUpcomingVisibleLimit(UPCOMING_PAGE_SIZE);
    setUpcomingPagination({
      publicCursor: null,
      hasMore: false
    });
    setReplayPagination({
      publicCursor: null,
      hasMore: false
    });
    setStoreCatalogLoaded(false);
    setLoadingStoreCatalog(false);
    setClubOpenMatches([]);
    setLoadingClubOpenMatches(!isTvView);

    const nowIso = new Date().toISOString();
    const selectedChannelQuery = selectedRtvChannelId ? {channelId: selectedRtvChannelId} : {};
    const publicCatalogBaseQuery = isTvView ? {view: 'tv'} : {};
    if (!isTvView) {
      api.resolvePublicPartner({slug: partnerSlug}).then(resolveRes => {
        if (requestId !== catalogRequestId.current || resolveRes.status !== 200 || !resolveRes.json.partner) {
          return;
        }
        setCatalog(currentCatalog => {
          if (currentCatalog.partner && currentCatalog.partner.slug === resolveRes.json.partner.slug) {
            return currentCatalog;
          }
          return {
            partner: resolveRes.json.partner,
            items: [],
            specialEvents: []
          };
        });
      });
    }
    const rtvCatalogPromise = Promise.all([
      api.getPublicPartnerCatalog(partnerSlug, {
        ...publicCatalogBaseQuery,
        ...selectedChannelQuery,
        types: 'rtv,events',
        from: nowIso,
        limit: UPCOMING_PAGE_SIZE
      }, token),
      api.getPublicPartnerCatalog(partnerSlug, {
        ...publicCatalogBaseQuery,
        ...selectedChannelQuery,
        types: 'rtv,events',
        to: nowIso,
        limit: UPCOMING_PAGE_SIZE
      }, token)
    ]);
    const [mainRes, replayRes] = await rtvCatalogPromise;

    if (requestId !== catalogRequestId.current) {
      return;
    }

    if (mainRes.status === 200 && replayRes.status === 200) {
      const partner = mainRes.json.partner || replayRes.json.partner || null;
      const publicItems = mergeCatalogItems(
        mainRes.json.items,
        replayRes.json.items
      );
      const publicUpcomingCursor = mainRes.json.nextCursor || null;
      const publicReplayCursor = replayRes.json.nextCursor || null;

      setCatalog({
        partner,
        items: publicItems,
        specialEvents: mainRes.json.specialEvents || replayRes.json.specialEvents || []
      });
      setUpcomingPagination({
        publicCursor: publicUpcomingCursor,
        hasMore: Boolean(publicUpcomingCursor)
      });
      setReplayPagination({
        publicCursor: publicReplayCursor,
        hasMore: Boolean(publicReplayCursor)
      });
      setLoadingCatalog(false);
      if (!isTvView && partner && partner.openMatchesEnabled) {
        const openMatchPartnerUid = partner.uid || partner.id;
        let openMatchRes = await api.getPublicOpenMatches({partnerId: openMatchPartnerUid, limit: 6});
        if (requestId !== catalogRequestId.current) {
          return;
        }
        if (openMatchRes.status !== 200 && token) {
          openMatchRes = await api.getOpenMatches({partnerId: openMatchPartnerUid}, token);
        }
        if (requestId !== catalogRequestId.current) {
          return;
        }
        setClubOpenMatches(openMatchRes.status === 200 ? openMatchRes.json.openMatches || [] : []);
      }
      setLoadingClubOpenMatches(false);
    } else if (mainRes.status === 404 || replayRes.status === 404) {
      setCatalogError(t('PUBLIC_CATALOG_PARTNER_NOT_FOUND'));
      setClubOpenMatches([]);
      setLoadingClubOpenMatches(false);
      setLoadingCatalog(false);
    } else {
      setCatalogError(t('PUBLIC_CATALOG_LOAD_ERROR_STATUS', {status: mainRes.status}));
      setClubOpenMatches([]);
      setLoadingClubOpenMatches(false);
      setLoadingCatalog(false);
    }
  };

  const loadMoreUpcoming = async () => {
    if (loadingMoreUpcoming || visibleUpcomingItems.length < groupedCatalogItems.upcoming.length) {
      setUpcomingVisibleLimit(currentLimit => currentLimit + UPCOMING_PAGE_SIZE);
      return;
    }

    if (!catalog.partner || !upcomingPagination.hasMore) {
      return;
    }

    setLoadingMoreUpcoming(true);
    const requestId = catalogRequestId.current;
    const nowIso = new Date().toISOString();
    const selectedChannelQuery = selectedRtvChannelId ? {channelId: selectedRtvChannelId} : {};
    const publicCursor = upcomingPagination.publicCursor;

    try {
      const publicRes = publicCursor
        ? await api.getPublicPartnerCatalog(partnerSlug, {
          ...(isTvView ? {view: 'tv'} : {}),
          ...selectedChannelQuery,
          types: 'rtv,events',
          from: nowIso,
          limit: UPCOMING_PAGE_SIZE,
          cursor: publicCursor
        }, token)
        : null;

      if (requestId !== catalogRequestId.current) {
        return;
      }

      const publicItems = publicRes && publicRes.status === 200 ? publicRes.json.items || [] : [];

      setCatalog(currentCatalog => ({
        ...currentCatalog,
        items: mergeCatalogItems(currentCatalog.items, publicItems)
      }));
      setUpcomingPagination(currentPagination => ({
        ...currentPagination,
        publicCursor: publicRes && publicRes.status === 200 ? publicRes.json.nextCursor : null,
        hasMore: Boolean(publicRes && publicRes.status === 200 && publicRes.json.nextCursor)
      }));
      setUpcomingVisibleLimit(currentLimit => currentLimit + UPCOMING_PAGE_SIZE);
    } finally {
      setLoadingMoreUpcoming(false);
    }
  };

  const loadMoreReplay = async () => {
    if (loadingMoreReplay || !catalog.partner || !replayPagination.hasMore) {
      return;
    }

    setLoadingMoreReplay(true);
    const requestId = catalogRequestId.current;
    const selectedChannelQuery = selectedRtvChannelId ? {channelId: selectedRtvChannelId} : {};

    try {
      const publicRes = await api.getPublicPartnerCatalog(partnerSlug, {
        ...(isTvView ? {view: 'tv'} : {}),
        ...selectedChannelQuery,
        types: 'rtv,events',
        to: new Date().toISOString(),
        limit: UPCOMING_PAGE_SIZE,
        cursor: replayPagination.publicCursor
      }, token);

      if (requestId !== catalogRequestId.current) {
        return;
      }

      const publicItems = publicRes && publicRes.status === 200 ? publicRes.json.items || [] : [];
      setCatalog(currentCatalog => ({
        ...currentCatalog,
        items: mergeCatalogItems(currentCatalog.items, publicItems)
      }));
      setReplayPagination({
        publicCursor: publicRes && publicRes.status === 200 ? publicRes.json.nextCursor : null,
        hasMore: Boolean(publicRes && publicRes.status === 200 && publicRes.json.nextCursor)
      });
    } finally {
      setLoadingMoreReplay(false);
    }
  };

  const loadAffiliations = async () => {
    setLoadingAffiliations(true);
    const res = await api.getPublicPartnerAffiliations(partnerSlug, token);
    setAffiliations(res.status === 200 ? res.json.affiliations || [] : []);
    setLoadingAffiliations(false);
  };

  const redirectToLoginWithResume = resumeToken => {
    rememberClubPath();
    const nextPath = resumeToken
      ? `${safeNextPath}?resume=${encodeURIComponent(resumeToken)}`
      : safeNextPath;

    if (!isSafePath(nextPath)) {
      return;
    }

    history.push(`/login?next=${encodeURIComponent(nextPath)}`);
  };

  const redirectToSignUpWithResume = () => {
    rememberClubPath();
    history.push(`/sign-up-player?next=${encodeURIComponent(safeNextPath)}`);
  };

  const updateContentAccessFromPlayIntent = (result, sourceItem = null) => {
    if (
      !result
      || result.status !== 200
      || !result.json
      || result.json.action !== 'play_allowed'
      || !result.json.playback
    ) {
      return;
    }

    const matchingItem = sourceItem || (() => {
      const playback = result.json.playback;
      const contentType = playback.videoPartnerId ? 'partner_video' : null;
      const contentId = playback.videoPartnerId || playback.slotId;
      if (!contentId) {
        return null;
      }
      return catalog.items.find(item => (
        String(item.contentId) === String(contentId)
        && (!contentType || item.contentType === contentType)
      ));
    })();
    if (!matchingItem) {
      return;
    }

    setContentAccess(previousAccess => ({
      ...previousAccess,
      [`${matchingItem.contentType}:${matchingItem.contentId}`]: result.json
    }));
  };

  const handlePlayIntentResult = (result, pendingPlayerWindow = null, sourceItem = null) => {
    if (result.status !== 200 || !result.json || !result.json.action) {
      if (pendingPlayerWindow && !pendingPlayerWindow.closed) {
        pendingPlayerWindow.close();
      }
      setNotice({
        variant: 'danger',
        message: result.status === 429
          ? t('PUBLIC_CATALOG_TOO_MANY_REQUESTS')
          : t('PUBLIC_CATALOG_PLAYBACK_CHECK_ERROR', {status: result.status})
      });
      return;
    }

    if (result.json.action === 'login_required') {
      if (pendingPlayerWindow && !pendingPlayerWindow.closed) {
        pendingPlayerWindow.close();
      }
      setPaywallOffer(null);
      redirectToLoginWithResume(result.json.resumeToken);
      return;
    }

    if (result.json.action === 'paywall') {
      if (pendingPlayerWindow && !pendingPlayerWindow.closed) {
        pendingPlayerWindow.close();
      }
      if (result.json.reasonCode === 'channel_blocked') {
        const isProcessing = sourceItem && (
          sourceItem.__catalogSection === 'live'
          || sourceItem.__catalogSection === 'upcoming'
          || sourceItem.videoComplete === false
        );
        setPaywallOffer(null);
        setVideoUnavailableNotice({
          title: t(isProcessing ? 'PUBLIC_CATALOG_VIDEO_PROCESSING' : 'PUBLIC_CATALOG_VIDEO_UNAVAILABLE'),
          message: isProcessing
            ? t('PUBLIC_CATALOG_VIDEO_PROCESSING_RETRY')
            : t('PUBLIC_CATALOG_MATCH_UNAVAILABLE')
        });
        return;
      }
      const fallback = t('PUBLIC_CATALOG_PROFILE_CONTENT_UNAVAILABLE');
      setPaywallOffer(result.json);
      setNotice({
        variant: 'warning',
        message: PAYWALL_MESSAGE[result.json.reasonCode] ? t(PAYWALL_MESSAGE[result.json.reasonCode]) : fallback
      });
      return;
    }

    if (result.json.action === 'play_allowed' && result.json.playback && result.json.playback.krpanoToken) {
      setPaywallOffer(null);
      updateContentAccessFromPlayIntent(result, sourceItem);
      const playerUrl = buildPlayerUrl(result.json.playback);
      if (pendingPlayerWindow && !pendingPlayerWindow.closed) {
        pendingPlayerWindow.location.href = playerUrl;
      } else {
        openVideoPlayer(playerUrl, 'noopener,noreferrer');
      }
      setNotice({
        variant: 'success',
        message: t('PUBLIC_CATALOG_PLAYBACK_STARTED')
      });
      return;
    }

    if (pendingPlayerWindow && !pendingPlayerWindow.closed) {
      pendingPlayerWindow.close();
    }
    setNotice({
      variant: 'danger',
      message: t('PUBLIC_CATALOG_INVALID_SERVER_RESPONSE')
    });
  };

  const onPlay = async (item, section = null) => {
    const actionId = `${item.contentType}:${item.contentId}`;
    if (playActionLoadingId === actionId) {
      return;
    }
    setNotice(null);
    setShowTopUp(false);
    if (item.playback && item.playback.krpanoToken) {
      openVideoPlayer(buildPlayerUrl(item.playback), 'noopener,noreferrer');
      return;
    }
    if (token || isFreeCatalogItem(item)) {
      preconnectVideoPlayer();
      const pendingPlayerWindow = window.open('', '_blank');
      if (pendingPlayerWindow) {
        pendingPlayerWindow.opener = null;
      }
      setPlayActionLoadingId(actionId);
      const result = await api.postPublicPlayIntent({
        contentId: item.contentId,
        contentType: item.contentType
      }, token);
      setPlayActionLoadingId(null);
      handlePlayIntentResult(result, pendingPlayerWindow, {...item, __catalogSection: section});
      return;
    }

    const isAcademy = item.contentType === 'academy_slot';
    setPaywallOffer({
      action: 'paywall',
      contentId: item.contentId,
      contentType: item.contentType,
      reasonCode: isAcademy ? 'affiliation_required' : 'no_subscription',
      offers: {
        subscriptions: item.commerceFlags ? item.commerceFlags.subscriptionOffers || [] : [],
        singlePurchase: item.commerceFlags && item.commerceFlags.singlePurchaseAvailable
          ? {available: true, priceEur: item.commerceFlags.singlePurchasePriceEur}
          : null
      },
      requiredAffiliation: isAcademy ? {
        affiliationName: t('PUBLIC_CATALOG_COACH_PROFILE'),
        status: 'available',
        requestAllowed: false
      } : null
    });
  };

  const copySlotToMyVideo = item => {
    dispatch(addVideoToPlayerArea(item.contentId));
  };

  const subscribeVideoReadyNotification = async item => {
    if (!item) return;
    if (!token) {
      savePendingActionAndLogin({
        type: 'video_ready_notification',
        contentId: item.contentId,
        contentType: item.contentType
      });
      return;
    }
    setVideoReadyNotificationLoadingId(item.contentId);
    const result = await api.postPublicVideoReadyNotification(item.contentId, token);
    setVideoReadyNotificationLoadingId(null);
    if (result.status === 200 || result.status === 201) {
      setNotice({
        variant: result.json && result.json.videoComplete ? 'success' : 'success',
        message: result.json && result.json.videoComplete
          ? t('PUBLIC_CATALOG_VIDEO_ALREADY_READY')
          : t('PUBLIC_CATALOG_READY_REQUEST_SAVED'),
        action: 'video_ready_notification',
        contentId: item.contentId,
        contentType: item.contentType,
        contentSection: result.json && result.json.videoComplete ? 'ready' : 'saved'
      });
      return;
    }
    if (result.status === 401) {
      savePendingActionAndLogin({
        type: 'video_ready_notification',
        contentId: item.contentId,
        contentType: item.contentType
      });
      return;
    }
    setNotice({
      variant: 'warning',
      message: t('PUBLIC_CATALOG_NOTIFICATION_SAVE_ERROR')
    });
  };

  const showVideoProcessingNotice = (item, section) => {
    setNotice({
      variant: 'info',
      title: t(section === 'live' ? 'PUBLIC_CATALOG_UPLOAD_IN_PROGRESS' : 'PUBLIC_CATALOG_VIDEO_PROCESSING'),
      message: section === 'live'
        ? t('PUBLIC_CATALOG_LIVE_PROCESSING_HELP')
        : t('PUBLIC_CATALOG_PROCESSING_MY_VIDEO_HELP'),
      action: 'video_processing_status',
      contentId: item.contentId,
      contentType: item.contentType,
      contentSection: section
    });
  };

  const createSlotHighlight = item => async () => {
    if (item.playback && item.playback.krpanoToken) {
      dispatch(permanentVideoSelected({
        videoId: null,
        slotId: item.contentId,
        sliceHighlight: true,
        name: getCatalogItemTitle(item, t('PUBLIC_CATALOG_DEFAULT_MATCH_TITLE')),
        token: item.playback.krpanoToken,
        cameras: item.selectedCameras || item.cameras || [],
      }));
      dispatch(showVideoHighlightModal());
      return;
    }
    if (!token) {
      savePendingActionAndLogin({
        type: 'verify_access',
        contentId: item.contentId,
        contentType: item.contentType
      });
      return;
    }
    setPlayActionLoadingId(`${item.contentType}:${item.contentId}`);
    const result = await api.postPublicPlayIntent({
      contentId: item.contentId,
      contentType: item.contentType
    }, token);
    setPlayActionLoadingId(null);
    if (result.status !== 200 || !result.json || result.json.action !== 'play_allowed') {
      handlePlayIntentResult(result);
      return;
    }
    dispatch(permanentVideoSelected({
      videoId: null,
      slotId: item.contentId,
      sliceHighlight: true,
      name: getCatalogItemTitle(item, t('PUBLIC_CATALOG_DEFAULT_MATCH_TITLE')),
      token: result.json.playback ? result.json.playback.krpanoToken : null,
      cameras: item.selectedCameras || item.cameras || [],
    }));
    dispatch(showVideoHighlightModal());
  };

  const createSlotAnalysis = item => async () => {
    if (item.playback && item.playback.krpanoToken) {
      dispatch(permanentVideoSelected({
        videoId: null,
        slotId: item.contentId,
        contentType: item.contentType,
        contentId: item.contentId,
        analysisMode: true,
        sliceHighlight: true,
        name: getCatalogItemTitle(item, t('PUBLIC_CATALOG_DEFAULT_MATCH_TITLE')),
        analysisTitle: t('PUBLIC_CATALOG_ANALYSIS_TITLE', {title: getCatalogItemTitle(item, t('PUBLIC_CATALOG_DEFAULT_MATCH_TITLE'))}),
        token: item.playback.krpanoToken,
        cameras: item.selectedCameras || item.cameras || [],
      }));
      dispatch(showVideoHighlightModal());
      return;
    }
    if (!token) {
      savePendingActionAndLogin({
        type: 'verify_access',
        contentId: item.contentId,
        contentType: item.contentType
      });
      return;
    }
    setPlayActionLoadingId(`${item.contentType}:${item.contentId}`);
    const result = await api.postPublicPlayIntent({
      contentId: item.contentId,
      contentType: item.contentType
    }, token);
    setPlayActionLoadingId(null);
    if (result.status !== 200 || !result.json || result.json.action !== 'play_allowed') {
      handlePlayIntentResult(result);
      return;
    }
    dispatch(permanentVideoSelected({
      videoId: null,
      slotId: item.contentId,
      contentType: item.contentType,
      contentId: item.contentId,
      analysisMode: true,
      sliceHighlight: true,
      name: getCatalogItemTitle(item, t('PUBLIC_CATALOG_DEFAULT_MATCH_TITLE')),
      analysisTitle: t('PUBLIC_CATALOG_ANALYSIS_TITLE', {title: getCatalogItemTitle(item, t('PUBLIC_CATALOG_DEFAULT_MATCH_TITLE'))}),
      token: result.json.playback ? result.json.playback.krpanoToken : null,
      cameras: item.selectedCameras || item.cameras || [],
    }));
    dispatch(showVideoHighlightModal());
  };

  const savePendingActionAndLogin = action => {
    activateAuthenticatedClubPage();
    window.sessionStorage.setItem(CLUB_PENDING_ACTION_KEY, JSON.stringify({
      partnerSlug,
      ...action
    }));
    redirectToLoginWithResume();
  };

  const requestAuthentication = action => {
    setAuthenticationRequired({
      action,
      paywallOffer
    });
    setPaywallOffer(null);
  };

  const closeAuthenticationRequired = () => {
    if (authenticationRequired && authenticationRequired.paywallOffer) {
      setPaywallOffer(authenticationRequired.paywallOffer);
    }
    setAuthenticationRequired(null);
  };

  const continueAuthentication = destination => {
    if (!authenticationRequired || !authenticationRequired.action) return;
    activateAuthenticatedClubPage();
    window.sessionStorage.setItem(CLUB_PENDING_ACTION_KEY, JSON.stringify({
      partnerSlug,
      ...authenticationRequired.action
    }));
    setAuthenticationRequired(null);
    if (profile && profile.user && [ROLES.partner, ROLES.admin].includes(profile.user.role)) {
      dispatch(logout());
    }
    if (destination === 'signup') {
      redirectToSignUpWithResume();
      return;
    }
    redirectToLoginWithResume();
  };

  const verifySelectedContentAccess = async () => {
    if (!paywallOffer || !paywallOffer.contentId || !paywallOffer.contentType) return;
    if (!token) {
      savePendingActionAndLogin({
        type: 'verify_access',
        contentId: paywallOffer.contentId,
        contentType: paywallOffer.contentType
      });
      return;
    }
    activateAuthenticatedClubPage();
    setPlayActionLoadingId(`${paywallOffer.contentType}:${paywallOffer.contentId}`);
    const result = await api.postPublicPlayIntent({
      contentId: paywallOffer.contentId,
      contentType: paywallOffer.contentType
    }, token);
    setPlayActionLoadingId(null);
    const item = catalog.items.find(catalogItem => (
      catalogItem.contentId === paywallOffer.contentId
      && catalogItem.contentType === paywallOffer.contentType
    ));
    updateContentAccessFromPlayIntent(result, item);
    setPaywallOffer(null);
    setNotice({
      variant: result.json && result.json.action === 'play_allowed' ? 'success' : 'info',
      message: result.json && result.json.action === 'play_allowed'
        ? t('PUBLIC_CATALOG_ACCESS_VERIFIED_PLAY')
        : t('PUBLIC_CATALOG_ACCESS_VERIFIED_OPTIONS')
    });
  };

  const openSubscriptionOptionsModal = () => {
    if (!subscriptionOptionsItem || !subscriptionOptionsItem.commerceFlags) return;
    setNotice(null);
    setShowTopUp(false);
    setPaywallOffer({
      action: 'paywall',
      contentId: subscriptionOptionsItem.contentId,
      contentType: subscriptionOptionsItem.contentType,
      reasonCode: 'subscription_options',
      managementMode: true,
      offers: {
        subscriptions: subscriptionOptionsItem.commerceFlags.subscriptionOffers || [],
        singlePurchase: null
      },
      requiredAffiliation: null
    });
  };

  const openCatalogItemVisionOptions = item => {
    const commerceFlags = item.commerceFlags || {};
    setNotice(null);
    setShowTopUp(false);
    setPaywallOffer({
      action: 'paywall',
      contentId: item.contentId,
      contentType: item.contentType,
      reasonCode: 'subscription_options',
      offers: {
        subscriptions: commerceFlags.subscriptionOffers || [],
        singlePurchase: commerceFlags.singlePurchaseAvailable
          ? {available: true, priceEur: commerceFlags.singlePurchasePriceEur}
          : null
      },
      requiredAffiliation: null
    });
  };

  const loadContentAccess = async targetItem => {
    if (!token || !catalog.items.length) {
      setContentAccess({});
      return;
    }
    setLoadingContentAccess(true);
    try {
      const itemsToCheck = targetItem ? [targetItem] : [];
      if (itemsToCheck.length === 0) {
        return;
      }
      const results = await Promise.all(itemsToCheck.map(async item => ({
        key: `${item.contentType}:${item.contentId}`,
        result: await api.postPublicPlayIntent({
          contentId: item.contentId,
          contentType: item.contentType
        }, token)
      })));
      setContentAccess(currentAccess => results.reduce((access, entry) => ({
        ...access,
        [entry.key]: entry.result.status === 200 ? entry.result.json : null
      }), currentAccess));
    } finally {
      setLoadingContentAccess(false);
    }
  };

  const handlePurchaseResult = async (result, successMessage) => {
    if (result.status === 200 || result.status === 201) {
      setShowTopUp(false);
      dispatch(refreshPlayerProfile());
      setNotice({variant: 'success', message: successMessage});
      const item = paywallOffer ? catalog.items.find(catalogItem => (
        catalogItem.contentId === paywallOffer.contentId
        && catalogItem.contentType === paywallOffer.contentType
      )) : null;
      if (item) {
        await loadContentAccess(item);
      }
      setPaywallOffer(null);
      return;
    }

    if (result.json && ['insufficient_funds', 'not_enough_credit'].includes(result.json.error)) {
      setShowTopUp(true);
      setNotice(null);
      return;
    }

    setNotice({
      variant: 'danger',
      message: t('PUBLIC_CATALOG_PURCHASE_ERROR', {error: result.json && result.json.error ? result.json.error : result.status})
    });
  };

  const canPurchaseAsPlayer = () => {
    if (profile && profile.user && profile.user.role === ROLES.player) {
      return true;
    }
    setNotice({
      variant: 'warning',
      message: t('PUBLIC_CATALOG_PLAYER_PROFILE_REQUIRED')
    });
    return false;
  };

  const purchaseSingleVideo = async () => {
    if (!paywallOffer || !paywallOffer.contentId || !paywallOffer.contentType) return;
    if (!token) {
      requestAuthentication({
        type: 'single_purchase',
        contentId: paywallOffer.contentId,
        contentType: paywallOffer.contentType,
        priceEur: paywallOffer.offers.singlePurchase.priceEur
      });
      return;
    }
    if (!canPurchaseAsPlayer()) return;
    activateAuthenticatedClubPage();
    const price = paywallOffer.offers && paywallOffer.offers.singlePurchase
      ? paywallOffer.offers.singlePurchase.priceEur
      : null;
    if (!window.confirm(t('PUBLIC_CATALOG_CONFIRM_VIDEO_PURCHASE', {price: formatEur(price, locale)}))) {
      return;
    }
    const actionId = `purchase:${paywallOffer.contentType}:${paywallOffer.contentId}`;
    setPurchaseActionId(actionId);
    const result = paywallOffer.contentType === 'partner_video'
      ? await api.purchasePartnerVideo(Number(paywallOffer.contentId), token)
      : await api.purchaseVideo([Number(paywallOffer.contentId)], token);
    setPurchaseActionId(null);
    await handlePurchaseResult(result, t('PUBLIC_CATALOG_VIDEO_PURCHASED_OPENING'));
  };

  const purchaseSubscription = async offer => {
    const kindMap = {
      rtv_dedicated: subscriptionKind.rtvDedicated,
      rtv_special_event: subscriptionKind.rtvEvent,
      rtv_unlimited: subscriptionKind.rtvUnlimited
    };
    if (!token) {
      requestAuthentication({
        type: 'subscription',
        contentId: paywallOffer.contentId,
        contentType: paywallOffer.contentType,
        offer
      });
      return;
    }
    if (!canPurchaseAsPlayer()) return;
    activateAuthenticatedClubPage();
    const durationLabel = offer.months
      ? t('PUBLIC_CATALOG_MONTHS', {count: offer.months})
      : t('PUBLIC_CATALOG_MONTHS', {count: 1});
    const priceLabel = offer.kind === 'rtv_special_event'
      ? t('PUBLIC_CATALOG_EVENT_ACCESS_PRICE', {
        price: formatEur(offer.priceEur, locale),
        start: new Date(offer.accessStartsAt).toLocaleString(locale),
        end: new Date(offer.accessEndsAt).toLocaleString(locale)
      })
      : offer.planId
        ? t('PUBLIC_CATALOG_TOTAL_FOR_DURATION', {price: formatEur(offer.priceEur, locale), duration: durationLabel})
        : t('PUBLIC_CATALOG_PER_MONTH_PRICE', {price: formatEur(offer.priceEur, locale)});
    if (!window.confirm(t('PUBLIC_CATALOG_CONFIRM_SUBSCRIPTION', {price: priceLabel}))) {
      return;
    }
    const invoiceChoice = askInvoiceRequest(profile);
    if (invoiceChoice.blocked) return;
    const actionId = `subscription:${offer.kind}:${offer.planId || offer.channelId || 'all'}:${offer.eventId || ''}`;
    setPurchaseActionId(actionId);
    const payload = offer.kind === 'rtv_unlimited'
      ? {kind: kindMap[offer.kind]}
      : {kind: kindMap[offer.kind], partner: offer.partnerId, rtvChannel: offer.channelId, ...(offer.eventId ? {eventId: offer.eventId} : {})};
    const result = offer.planId
      ? await api.purchaseDedicatedSubscriptionPlan(offer.planId, token, invoiceChoice.invoiceRequested)
      : await api.setPlayerSubscription(payload, token, invoiceChoice.invoiceRequested);
    setPurchaseActionId(null);
    await handlePurchaseResult(result, t('PUBLIC_CATALOG_SUBSCRIPTION_ACTIVATED_OPENING'));
  };

  const requestPartnerAffiliation = async (affiliation, playerOnlinePayment = false) => {
    if (!token || !profile) {
      activateAuthenticatedClubPage();
      redirectToLoginWithResume();
      return;
    }
    activateAuthenticatedClubPage();
    if (!profile.uid || !profile.user || profile.user.role !== ROLES.player) {
      return;
    }

    setAffiliationRequestError(null);
    setRequestingAffiliationId(affiliation.affiliationId);
    const isRenewal = affiliation.renewalAllowed === true;
    const result = isRenewal
      ? await api.renewAffiliation(
        profile.uid,
        affiliation.affiliationId,
        playerOnlinePayment,
        token
      )
      : await api.requestAffiliation(
        affiliation.affiliationId,
        profile.uid,
        playerOnlinePayment,
        token
      );
    setRequestingAffiliationId(null);

    if (result.status === 201) {
      await loadAffiliations();
      dispatch(refreshPlayerProfile());
      setPaywallOffer(null);
      setAffiliationToConfirm(null);
      setNotice({
        variant: 'success',
        message: isRenewal
          ? t('PUBLIC_CATALOG_AFFILIATION_RENEWAL_SENT', {name: affiliation.name})
          : playerOnlinePayment
            ? t('PUBLIC_CATALOG_AFFILIATION_PAID_SENT', {name: affiliation.name})
            : t('PUBLIC_CATALOG_AFFILIATION_SENT', {name: affiliation.name})
      });
      return;
    }

    if (result.json && result.json.error === 'renew_already_requested') {
      await loadAffiliations();
      setAffiliationToConfirm(null);
      setNotice({
        variant: 'info',
        message: t('AFFILIATION_RENEWAL_ALREADY_REQUESTED')
      });
      return;
    }

    if (result.json && result.json.error === 'not_enough_credit') {
      setAffiliationRequestError(t('PUBLIC_CATALOG_AFFILIATION_CREDIT_ERROR'));
      return;
    }

    setAffiliationRequestError(t('PUBLIC_CATALOG_AFFILIATION_SEND_ERROR', {name: affiliation.name, status: result.status}));
  };

  const openAffiliationConfirmation = affiliation => {
    setAffiliationRequestError(null);
    setAffiliationToConfirm(affiliation);
  };

  const confirmAffiliationRequest = async (playerOnlinePayment = false) => {
    if (!affiliationToConfirm) return;
    await requestPartnerAffiliation(affiliationToConfirm, playerOnlinePayment);
  };

  const affiliationConfirmationDescription = affiliation => {
    if (!affiliation || !catalog.partner) return '';
    if (affiliation.renewalAllowed) {
      return t('PUBLIC_CATALOG_AFFILIATION_RENEWAL_DESCRIPTION', {
        club: catalog.partner.name,
        profile: affiliation.name
      });
    }
    if (affiliation.type === 'coach') {
      return t('PUBLIC_CATALOG_COACH_AFFILIATION_DESCRIPTION', {club: catalog.partner.name});
    }
    if (affiliation.type === 'rtv') {
      return t('PUBLIC_CATALOG_REPLAYERTV_AFFILIATION_DESCRIPTION', {club: catalog.partner.name});
    }
    return t('PUBLIC_CATALOG_GENERIC_AFFILIATION_DESCRIPTION', {profile: affiliation.name, club: catalog.partner.name});
  };

  catalogHandlers.current = {
    activateAuthenticatedClubPage,
    handlePlayIntentResult,
    loadAffiliations,
    loadCatalog,
    loadContentAccess,
    openBookingCalendar,
    refreshCatalogThumbnails,
    rememberClubPath,
    subscribeVideoReadyNotification
  };

  const renderMatchCard = (item, section) => {
    const cardTitle = getCatalogItemTitle(item, t('PUBLIC_CATALOG_DEFAULT_MATCH_TITLE'));
    const cardDescription = getCatalogItemDescription(item, cardTitle, t('PUBLIC_CATALOG_DEFAULT_MATCH_DESCRIPTION'));
    const cardVenue = getCatalogItemVenueLabel(item);
    const access = contentAccess[`${item.contentType}:${item.contentId}`];
    const accessLoading = token && (authenticatedClubPage || isTvView)
      && loadingContentAccess
      && access === undefined;
    const freeContent = isFreeCatalogItem(item);
    const videoDuration = getCatalogItemDuration(item);
    const videoFileSize = getCatalogItemFileSize(item);
    const fieldSport = getVideoFieldSport(item);
    const pickleballVideo = isPickleballVideo(item);
    const showCardDescription = cardDescription
      && cardDescription !== t('PUBLIC_CATALOG_DEFAULT_MATCH_DESCRIPTION');
    const partnerLogoUrl = catalog.partner && catalog.partner.logoUrl
      ? `${Configuration.apiEndPoint}/images/${catalog.partner.id}?load=${encodeURIComponent(catalog.partner.logoUrl)}`
      : null;

    const actionLoading = playActionLoadingId === `${item.contentType}:${item.contentId}`;
    const {
      canCreateAnalysis,
      canCreateHighlight,
      hasAccess,
      playbackStatus,
      playbackStatusBadgeVariant,
      playbackStatusLabel,
      primaryActionLabel,
      primaryActionDisabled,
      shouldOpenVisionOptions,
      showProcessingAction,
      singlePurchaseAvailable,
      subscriptionOffers,
      unavailableActionLabel,
      videoReadyForMyVideo,
      videoUnavailable
    } = resolvePublicPartnerVideoCardState({
      access,
      accessLoading,
      actionLoading,
      activeSubscription: hasActiveSubscriptionForCatalogItem(item, subscriptions),
      freeContent,
      item,
      pickleballVideo,
      section
    });
    const badges = [];
    if (section === 'live') badges.push({id: 'live', label: 'LIVE', tone: 'live'});
    if (section === 'upcoming') badges.push({id: 'upcoming', label: t('PUBLIC_CATALOG_UPCOMING'), tone: 'upcoming'});
    if (playbackStatusLabel && section !== 'upcoming') {
      badges.push({id: 'playback', label: t(VIDEO_CARD_LABEL_KEYS[playbackStatusLabel] || playbackStatusLabel), tone: playbackStatusBadgeVariant});
    }
    badges.push({
      id: 'content-type',
      label: CONTENT_TYPE_LABEL[item.contentType] ? t(CONTENT_TYPE_LABEL[item.contentType]) : item.contentType,
      tone: item.contentType === 'academy_slot' ? 'coach' : 'type'
    });
    if (!accessLoading && !videoUnavailable) {
      if (freeContent) badges.push({id: 'free', label: t('PUBLIC_CATALOG_FREE'), tone: 'free'});
      else if (hasAccess) badges.push({id: 'owned', label: t('PUBLIC_CATALOG_ACCESS_ACTIVE'), tone: 'owned'});
      else if (singlePurchaseAvailable) {
        badges.push({
          id: 'purchase',
          label: t('PUBLIC_CATALOG_BUY_PRICE', {price: formatEur(item.commerceFlags.singlePurchasePriceEur, locale)}),
          tone: 'purchase'
        });
      } else if (subscriptionOffers.length > 0) {
        badges.push({id: 'subscription', label: t('PUBLIC_CATALOG_SUBSCRIPTION'), tone: 'subscription'});
      }
    }
    const metadata = [
      item.startDatetime ? {id: 'datetime', value: formatDateTime(item.startDatetime, locale, t('PUBLIC_CATALOG_DATE_UNAVAILABLE'))} : null,
      cardVenue ? {id: 'venue', value: cardVenue} : null,
      videoFileSize ? {id: 'filesize', value: videoFileSize} : null
    ].filter(Boolean);
    const accessSummary = accessLoading
      ? t('PUBLIC_CATALOG_ACCESS_CHECKING')
      : videoUnavailable
        ? t(VIDEO_CARD_LABEL_KEYS[unavailableActionLabel] || unavailableActionLabel)
        : hasAccess
          ? t('PUBLIC_CATALOG_CONTENT_IN_ACCOUNT')
          : freeContent
            ? t('PUBLIC_CATALOG_FREE_ACCESS')
            : singlePurchaseAvailable && subscriptionOffers.length > 0
              ? t('PUBLIC_CATALOG_SINGLE_OR_SUBSCRIPTION', {price: formatEur(item.commerceFlags.singlePurchasePriceEur, locale)})
              : singlePurchaseAvailable
                ? t('PUBLIC_CATALOG_SINGLE_PRICE', {price: formatEur(item.commerceFlags.singlePurchasePriceEur, locale)})
                : subscriptionOffers.length > 0
                  ? t('PUBLIC_CATALOG_WITH_SUBSCRIPTION')
                  : null;
    const secondaryActions = [];
    if (canCreateHighlight) {
      secondaryActions.push({
        disabled: actionLoading,
        id: 'highlight',
        label: 'Highlight',
        onClick: createSlotHighlight(item)
      });
    }
    if (canCreateAnalysis) {
      secondaryActions.push({
        disabled: actionLoading,
        id: 'analysis',
        label: t('PUBLIC_CATALOG_MATCH_ANALYSIS'),
        onClick: createSlotAnalysis(item)
      });
    }
    if (videoReadyForMyVideo) {
      secondaryActions.push({
        disabled: false,
        id: 'my-video',
        label: t('PUBLIC_CATALOG_COPY_MY_VIDEO'),
        onClick: () => copySlotToMyVideo(item)
      });
    }
    if (showProcessingAction) {
      secondaryActions.push({
        disabled: false,
        id: 'processing',
        label: t(section === 'live' ? 'PUBLIC_CATALOG_UPLOAD_IN_PROGRESS' : 'PUBLIC_CATALOG_VIDEO_PROCESSING'),
        onClick: () => showVideoProcessingNotice(item, section)
      });
    }

    const cardViewModel = {
      accessSummary,
      badges,
      description: showCardDescription ? cardDescription : null,
      duration: videoDuration,
      fieldSport,
      logoAlt: catalog.partner ? t('PUBLIC_CATALOG_CLUB_LOGO_ALT', {club: catalog.partner.name}) : '',
      logoUrl: partnerLogoUrl,
      metadata,
      metadataAriaLabel: t('PUBLIC_CATALOG_VIDEO_DETAILS'),
      pbvisionEligible: pickleballVideo,
      primaryAction: {
        disabled: primaryActionDisabled,
        label: t(VIDEO_CARD_LABEL_KEYS[primaryActionLabel] || primaryActionLabel),
        onClick: () => (shouldOpenVisionOptions ? openCatalogItemVisionOptions(item) : onPlay(item, section)),
        tone: section === 'live' ? 'live' : 'default'
      },
      secondaryActions,
      showPlayIcon: false,
      sourceLabel: catalog.partner ? catalog.partner.name : 'ReplayerTV',
      thumbnailAlt: `Anteprima ${cardTitle}`,
      thumbnailFallbackUrl: getCatalogItemThumbnailFallbackUrl(item),
      thumbnailUrl: getCatalogItemThumbnailUrl(item),
      title: cardTitle,
      tone: section === 'live' ? 'live' : playbackStatus || 'default'
    };

    return (
      <Col className="replayer-club-page__video-column" md={6} lg={4} key={`${item.contentType}-${item.contentId}`}>
        <PublicPartnerSkyworkVideoCard card={cardViewModel}/>
      </Col>
    );
  };

  const activeCatalogSubscriptions = subscriptions && Array.isArray(subscriptions.subscriptions)
    ? subscriptions.subscriptions.filter(subscription => (
      subscription
      && new Date(subscription.created) <= new Date()
      && new Date(subscription.expiry) >= new Date()
    ))
    : [];
  const catalogSubscriptionOffers = subscriptionOptionsItem
    && subscriptionOptionsItem.commerceFlags
    && subscriptionOptionsItem.commerceFlags.subscriptionOffers
      ? subscriptionOptionsItem.commerceFlags.subscriptionOffers
      : [];
  const currentCatalogSubscription = activeCatalogSubscriptions
    .filter(subscription => (
      subscription.kind === subscriptionKind.rtvUnlimited
      || catalogSubscriptionOffers.some(offer => (
        Number(subscription.partner) === Number(offer.partnerId)
        && Number(subscription.rtv_channel_id) === Number(offer.channelId)
      ))
    ))
    .sort((a, b) => new Date(a.expiry) - new Date(b.expiry))[0];
  const currentCatalogSubscriptionExpiry = currentCatalogSubscription
    ? new Date(currentCatalogSubscription.expiry).toLocaleDateString(locale)
    : null;
  const activeSpecialEvents = Array.isArray(catalog.specialEvents)
    ? catalog.specialEvents
    : [];
  const hasSkyworkEvents = !isTvView
    && Boolean(catalog.partner)
    && activeSpecialEvents.length > 0;
  const hasBookableClubSections = !isTvView && Boolean(catalog.partner && catalog.partner.bookableFields);
  const hasCalendarClubSection = !isTvView && Boolean(catalog.partner && catalog.partner.calendarFields);
  const hasPlannerClubSection = hasBookableClubSections || hasCalendarClubSection;
  const hasOpenMatchClubSection = hasBookableClubSections && Boolean(catalog.partner && catalog.partner.openMatchesEnabled);
  const showAffiliationsClubSection = !isManagingOwnClub;
  const sectionNumbers = createClubSectionNumbers({
    hasEvents: hasSkyworkEvents,
    hasOpenMatches: hasOpenMatchClubSection,
    hasPlanner: hasPlannerClubSection,
    hasShop: Boolean(!isTvView && catalog.partner && catalog.partner.shopActive),
    showAffiliations: Boolean(!isTvView && catalog.partner && showAffiliationsClubSection)
  });
  const skyworkViewModel = createPublicPartnerSkyworkViewModel({
    apiEndPoint: Configuration.apiEndPoint,
    isTvView,
    partner: catalog.partner,
    rtvChannels,
    showBookingCalendar,
    showClubShowcase,
    showSubscriptionOptionsButton,
    tvSectionNumber: sectionNumbers.tv,
    translate: t
  });
  const skyworkAffiliations = affiliations.map(affiliation => {
    const requestState = createAffiliationRequestState(affiliation);
    const status = AFFILIATION_STATUS_TRANSLATION_KEY[requestState.status]
      ? requestState.status
      : 'unavailable';
    const requesting = requestingAffiliationId === affiliation.affiliationId;
    const profileName = getAffiliationProfileName(affiliation);
    const commercialDetails = createAffiliationCommercialDetails({
      affiliation,
      formatPrice: formatEur,
      translate: t
    });

    return {
      actionLabel: requesting
        ? t('CLUB_SKYWORK_AFFILIATION_REQUESTING')
        : requestState.action === 'request'
          ? t('CLUB_SKYWORK_AFFILIATION_REQUEST', {profile: profileName})
          : requestState.action === 'renew'
            ? t('CLUB_SKYWORK_AFFILIATION_RENEW', {profile: profileName})
            : t(AFFILIATION_STATUS_SHORT_TRANSLATION_KEY[status]),
      disabled: requestState.disabled || requesting,
      benefits: commercialDetails.benefits,
      icon: getAffiliationIcon(affiliation),
      id: affiliation.affiliationId,
      name: profileName,
      paymentLabel: commercialDetails.paymentLabel,
      status,
      statusLabel: t(AFFILIATION_STATUS_TRANSLATION_KEY[status]),
      statusShortLabel: t(AFFILIATION_STATUS_SHORT_TRANSLATION_KEY[status])
    };
  });
  const skyworkAffiliationLabels = {
    ariaLabel: t('CLUB_SKYWORK_AFFILIATIONS_ARIA'),
    empty: t('CLUB_SKYWORK_AFFILIATIONS_EMPTY'),
    eyebrow: t('CLUB_SKYWORK_AFFILIATIONS_EYEBROW'),
    intro: t('CLUB_SKYWORK_AFFILIATIONS_INTRO'),
    loading: t('CLUB_SKYWORK_AFFILIATIONS_LOADING'),
    number: sectionNumbers.affiliations,
    title: t('CLUB_SKYWORK_AFFILIATIONS_TITLE'),
    titleAccent: t('CLUB_SKYWORK_AFFILIATIONS_TITLE_ACCENT')
  };
  const skyworkJourneyLabels = {
    events: {
      available: t('CLUB_SKYWORK_EVENTS_AVAILABLE'),
      empty: t('CLUB_SKYWORK_EVENTS_EMPTY'),
      eyebrow: t('CLUB_SKYWORK_EVENTS_EYEBROW'),
      intro: t('CLUB_SKYWORK_EVENTS_INTRO'),
      live: t('CLUB_SKYWORK_EVENTS_LIVE'),
      number: sectionNumbers.events,
      title: t('CLUB_SKYWORK_EVENTS_TITLE'),
      titleAccent: null,
      upcoming: t('CLUB_SKYWORK_EVENTS_UPCOMING'),
      view: t('CLUB_SKYWORK_EVENTS_VIEW'),
      badge: t('SPECIAL_EVENTS'),
      access: t('CLUB_SKYWORK_EVENTS_ACCESS'),
      content: t('CLUB_SKYWORK_EVENTS_CONTENT')
    },
    planner: {
      eyebrow: hasBookableClubSections ? t('CLUB_SKYWORK_PLANNER_EYEBROW') : t('CLUB_SKYWORK_CALENDAR_EYEBROW'),
      hide: hasBookableClubSections ? t('CLUB_SKYWORK_PLANNER_HIDE') : t('CLUB_SKYWORK_CALENDAR_HIDE'),
      intro: hasBookableClubSections ? t('CLUB_SKYWORK_PLANNER_INTRO') : t('CLUB_SKYWORK_CALENDAR_INTRO'),
      number: sectionNumbers.planner,
      open: hasBookableClubSections ? t('CLUB_SKYWORK_PLANNER_OPEN') : t('CLUB_SKYWORK_CALENDAR_OPEN'),
      title: hasBookableClubSections ? t('CLUB_SKYWORK_PLANNER_TITLE') : t('CLUB_SKYWORK_CALENDAR_TITLE'),
      titleAccent: hasBookableClubSections ? t('CLUB_SKYWORK_PLANNER_TITLE_ACCENT') : t('CLUB_SKYWORK_CALENDAR_TITLE_ACCENT')
    },
    openMatch: {
      create: t('CLUB_SKYWORK_OPEN_MATCH_CREATE'),
      empty: t('CLUB_SKYWORK_OPEN_MATCH_EMPTY'),
      eyebrow: t('CLUB_SKYWORK_OPEN_MATCH_EYEBROW'),
      intro: t('CLUB_SKYWORK_OPEN_MATCH_INTRO'),
      loading: t('CLUB_SKYWORK_OPEN_MATCH_LOADING'),
      number: sectionNumbers.openMatches,
      title: t('CLUB_SKYWORK_OPEN_MATCH_TITLE'),
      titleAccent: t('CLUB_SKYWORK_OPEN_MATCH_TITLE_ACCENT')
    },
    shop: {
      eyebrow: t('CLUB_SKYWORK_SHOP_EYEBROW'),
      intro: t('CLUB_SKYWORK_SHOP_INTRO'),
      number: sectionNumbers.shop,
      open: t('CLUB_SKYWORK_SHOP_OPEN'),
      title: t('CLUB_SKYWORK_SHOP_TITLE'),
      titleAccent: t('CLUB_SKYWORK_SHOP_TITLE_ACCENT')
    }
  };

  const scrollToClubSection = targetId => {
    setTimeout(() => {
      const section = document.getElementById(targetId);
      if (section) {
        section.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
    }, 0);
  };

  const handleSkyworkAction = action => {
    if (!action || !catalog.partner) return;

    if (isManagingOwnClub) {
      if (action.action === 'booking' || action.action === 'planner') {
        openPartnerCustomerBooking();
        return;
      }
      if (action.action === 'affiliations') {
        history.push('/partner-affiliations');
        return;
      }
      if (action.action === 'catalog' || action.action === 'subscriptions') {
        history.push('/partner-replayer-tv-home');
        return;
      }
      if (action.action === 'store') {
        history.push('/partner-video-store');
        return;
      }
      if (action.action === 'open_match') {
        history.push('/partner-open-matches');
        return;
      }
    }

    if (action.action === 'catalog') {
      scrollToClubSection('club-matches');
      return;
    }

    if (action.action === 'booking') {
      if (hasBookableClubSections) {
        openClubQuickBooking();
      } else {
        openClubPlanner();
      }
      return;
    }

    if (action.action === 'planner') {
      openClubPlanner();
      return;
    }

    if (action.action === 'store') {
      openVideoStore();
      return;
    }

    if (action.action === 'subscriptions') {
      openSubscriptionOptionsModal();
      return;
    }

    if (action.action === 'showcase') {
      if (showClubShowcase) {
        setShowClubShowcase(false);
        return;
      }
      setShowClubShowcase(true);
      scrollToClubSection('club-showcase');
      return;
    }

    if (action.action === 'affiliations') {
      scrollToClubSection('club-affiliations');
      return;
    }

    if (action.action === 'shop') {
      history.push(`/shop/${catalog.partner.slug}`);
    }

    if (action.action === 'open_match') {
      history.push(`/open-matches/create?partnerId=${catalog.partner.uid || catalog.partner.id}`);
    }
  };

  const specialEventPartner = catalog.partner ? {
    ...catalog.partner,
    uid: catalog.partner.uid || catalog.partner.id
  } : null;
  const specialEventCards = activeSpecialEvents.map(event => {
    const context = specialEventHomeCardContext(
      event,
      specialEventPartner,
      subscriptions
    );
    const price = (context.priceCents / 100).toFixed(2);
    const scheduled = context.subscriptionStatus.status === subscriptionStatuses.scheduledEvent;
    return {
      accessRange: `${new Date(event.startDatetime).toLocaleDateString(locale)} – ${new Date(event.endDatetime).toLocaleDateString(locale)}`,
      actionLabel: context.canWatch
        ? t('WATCH_RTV')
        : scheduled
          ? t('HOME_REDESIGN_SPECIAL_EVENT_PURCHASED')
          : t('HOME_REDESIGN_SPECIAL_EVENT_BUY', {price}),
      contentRange: `${new Date(event.contentStartDatetime || event.startDatetime).toLocaleDateString(locale)} – ${new Date(event.contentEndDatetime || event.endDatetime).toLocaleDateString(locale)}`,
      description: event.description,
      id: event.id,
      imageUrl: event.imageName && event.imageName.length > 0
        ? `${Configuration.apiEndPoint}/partners/events/${event.id}/images?load=${encodeURIComponent(event.imageName[0])}`
        : null,
      priceLabel: t('HOME_REDESIGN_SPECIAL_EVENT_PRICE', {price}),
      title: event.title
    };
  });

  const handleSkyworkEventSelect = eventId => {
    const event = activeSpecialEvents.find(item => Number(item.id) === Number(eventId));
    if (!event || !specialEventPartner) {
      return;
    }
    const context = specialEventHomeCardContext(event, specialEventPartner, subscriptions);
    if (context.canWatch) {
      if (!token) {
        redirectToLoginWithResume();
        return;
      }
      const selectedPartner = partners.find(partner => (
        Number(partner.uid) === Number(event.uid)
      )) || specialEventPartner;
      dispatch(partnerSelected(selectedPartner));
      dispatch(rtvChannelSelected(context.channel));
      dispatch(specialEventSelected(event));
      dispatch(getRtvVideos(event.uid, event.rtvChannelId, event.id));
      rememberClubPath();
      history.push('/player-replayer-tv');
      return;
    }
    setNotice(null);
    setShowTopUp(false);
    setPaywallOffer({
      action: 'paywall',
      contentId: String(event.id),
      contentType: 'special_event',
      reasonCode: 'subscription_options',
      offers: {
        subscriptions: [createPublicSpecialEventOffer(event, context)],
        singlePurchase: null
      },
      requiredAffiliation: null
    });
  };

  const replayerTvContent = (
    <Fragment>
      {groupedCatalogItems.live.length > 0 && (
        <div id="club-live" className="public-partner-match-section public-partner-live-section" style={{scrollMarginTop: 110}}>
          <h3><span className="public-partner-live-dot"/>{t('PUBLIC_CATALOG_LIVE_NOW')}</h3>
          <Row>{groupedCatalogItems.live.map(item => renderMatchCard(item, 'live'))}</Row>
        </div>
      )}
      {groupedCatalogItems.delayed.length > 0 && (
        <section className="public-partner-match-section" aria-label={t('PUBLIC_CATALOG_LIVE_DELAYED')}>
          <h3>{t('PUBLIC_CATALOG_LIVE_DELAYED')}</h3>
          <p>{t('PUBLIC_CATALOG_LIVE_DELAYED_HELP')}</p>
          <Row>{groupedCatalogItems.delayed.map(item => renderMatchCard(item, 'delayed'))}</Row>
        </section>
      )}
      {selectedMatchListMode === 'replay' && groupedCatalogItems.replay.length > 0 && (
        <div className="public-partner-match-section">
          <h3>{t('PUBLIC_CATALOG_AVAILABLE_MATCHES')}</h3>
          <p>{t('PUBLIC_CATALOG_REPLAY_ORDER')}</p>
          <Row>{groupedCatalogItems.replay.map(item => renderMatchCard(item, 'replay'))}</Row>
          {replayPagination.hasMore && (
            <div style={{display: 'flex', justifyContent: 'center', marginTop: 8}}>
              <Button
                className="public-partner-load-more-button"
                variant="outline-primary"
                disabled={loadingMoreReplay}
                onClick={loadMoreReplay}
              >
                {loadingMoreReplay ? (
                  <Fragment>
                    <Spinner animation="border" size="sm" style={{marginRight: 8}} />
                    {t('LOADING')}
                  </Fragment>
                ) : t('PUBLIC_CATALOG_LOAD_MORE_MATCHES')}
              </Button>
            </div>
          )}
        </div>
      )}
      {selectedMatchListMode === 'upcoming' && groupedCatalogItems.upcoming.length > 0 && (
        <div className="public-partner-match-section">
          <h3>{t('PUBLIC_CATALOG_UPCOMING_MATCHES')}</h3>
          <p>{t('PUBLIC_CATALOG_UPCOMING_ORDER')}</p>
          <Row>{visibleUpcomingItems.map(item => renderMatchCard(item, 'upcoming'))}</Row>
          {canLoadMoreUpcoming && (
            <div style={{display: 'flex', justifyContent: 'center', marginTop: 8}}>
              <Button
                className="public-partner-load-more-button"
                variant="outline-primary"
                disabled={loadingMoreUpcoming}
                onClick={loadMoreUpcoming}
              >
                {loadingMoreUpcoming ? (
                  <Fragment>
                    <Spinner animation="border" size="sm" style={{marginRight: 8}} />
                    {t('LOADING')}
                  </Fragment>
                ) : t('PUBLIC_CATALOG_LOAD_MORE_MATCHES')}
              </Button>
            </div>
          )}
        </div>
      )}
      {(storeCatalogItems.length > 0 || loadingStoreCatalog || storeCatalogLoaded) && (
        <div id="club-video-store" className="public-partner-match-section public-partner-store-section">
          <div className="public-partner-store-heading">
            <Badge variant="info">Video Store</Badge>
            <h3>{t('PUBLIC_CATALOG_STORE_TITLE')}</h3>
            <p>{t('PUBLIC_CATALOG_STORE_INTRO')}</p>
          </div>
          {loadingStoreCatalog && (
            <div style={{display: 'flex', justifyContent: 'center', padding: 24}}>
              <Spinner animation="border" role="status"/>
            </div>
          )}
          {!loadingStoreCatalog && storeCatalogLoaded && storeCatalogItems.length === 0 && (
            <Alert variant="secondary">{t('PUBLIC_CATALOG_STORE_EMPTY')}</Alert>
          )}
          {groupedStoreCatalogItems.live.length > 0 && (
            <Fragment>
              <h4>{t('PUBLIC_CATALOG_LIVE_NOW')}</h4>
              <Row>{groupedStoreCatalogItems.live.map(item => renderMatchCard(item, 'live'))}</Row>
            </Fragment>
          )}
          {groupedStoreCatalogItems.delayed.length > 0 && (
            <div className="public-partner-match-section">
              <h3>{t('PUBLIC_CATALOG_LIVE_DELAYED')}</h3>
              <Row>{groupedStoreCatalogItems.delayed.map(item => renderMatchCard(item, 'delayed'))}</Row>
            </div>
          )}
          {groupedStoreCatalogItems.replay.length > 0 && (
            <Fragment>
              <h4>{t('PUBLIC_CATALOG_AVAILABLE_VIDEOS')}</h4>
              <Row>{groupedStoreCatalogItems.replay.map(item => renderMatchCard(item, 'replay'))}</Row>
            </Fragment>
          )}
          {groupedStoreCatalogItems.upcoming.length > 0 && (
            <Fragment>
              <h4>{t('PUBLIC_CATALOG_UPCOMING_PURCHASABLE')}</h4>
              <Row>{groupedStoreCatalogItems.upcoming.map(item => renderMatchCard(item, 'upcoming'))}</Row>
            </Fragment>
          )}
        </div>
      )}
    </Fragment>
  );

  const paywallSpecialEventPresentation = createPublicSpecialEventPaywallPresentation(
    paywallOffer,
    locale
  );

  return (
    <Fragment>
      <Helmet>
        <title>{catalog.partner ? `${catalog.partner.name} | Replayer` : 'Replayer catalogo pubblico'}</title>
      </Helmet>
      <Fade in appear className="bg_white" style={{transition: 'opacity 0.30s linear'}}>
        <Container fluid className="presentation-container replayer-club-page">
          <PublicPartnerSkyworkHero
            hero={skyworkViewModel ? skyworkViewModel.hero : null}
            loading={loadingCatalog}
            onAction={handleSkyworkAction}
          />
          {isManagingOwnClub ? (
            <section className="public-partner-management-strip" aria-label={t('PARTNER_CLUB_MANAGEMENT_TITLE')}>
              <div>
                <span className="public-partner-management-strip__eyebrow">{t('PARTNER_CLUB_MANAGEMENT_EYEBROW')}</span>
                <h2>{t('PARTNER_CLUB_MANAGEMENT_TITLE')}</h2>
                <p>{t('PARTNER_CLUB_MANAGEMENT_DESCRIPTION')}</p>
              </div>
              <div className="public-partner-management-strip__actions">
                <Button type="button" onClick={openPartnerCustomerBooking}>
                  <FaCalendarPlus aria-hidden="true"/>{t('PARTNER_BOOK_FOR_CUSTOMER')}
                </Button>
                <Button type="button" variant="outline-primary" onClick={() => history.push('/partner-fields')}>
                  <FaBuilding aria-hidden="true"/>{t('RESOURCES')}
                </Button>
                <Button type="button" variant="outline-primary" onClick={() => history.push('/partner-affiliations')}>
                  <FaUserCheck aria-hidden="true"/>{t('MEMBERSHIPS')}
                </Button>
                <Button type="button" variant="outline-primary" onClick={() => history.push('/partner-replayer-tv-home')}>
                  <FaTv aria-hidden="true"/>{t('REPLAYER_TV')}
                </Button>
              </div>
            </section>
          ) : null}
          <div className="main replayer-club-page__main">
            <div className="replayer-club-page__content">

                {notice && notice.action === 'video_ready_notification' && (
                  <Row style={{marginTop: 20}}>
                    <Col md={12}>
                      <div className={`public-partner-processing-notice public-partner-processing-notice-${notice.contentSection || notice.variant}`}>
                        <div className="public-partner-processing-icon">
                          {videoReadyNotificationLoadingId === notice.contentId
                            ? <Spinner animation="border" size="sm" />
                            : notice.contentSection === 'saved' || notice.contentSection === 'ready'
                              ? '✓'
                              : 'i'}
                        </div>
                        <div className="public-partner-processing-content">
                          <div className="public-partner-processing-kicker">
                            {notice.contentSection === 'saved' || notice.contentSection === 'ready'
                              ? t('PUBLIC_CATALOG_NOTIFICATION_ACTIVE')
                              : t('PUBLIC_CATALOG_VIDEO_STATUS')}
                          </div>
                          <h4>
                            {notice.contentSection === 'saved'
                              ? t('PUBLIC_CATALOG_REQUEST_SAVED')
                              : notice.contentSection === 'ready'
                              ? t('PUBLIC_CATALOG_VIDEO_READY')
                              : notice.contentSection === 'live'
                              ? t('PUBLIC_CATALOG_UPLOAD_IN_PROGRESS')
                              : t('PUBLIC_CATALOG_VIDEO_PROCESSING')}
                          </h4>
                          <p>{notice.message}</p>
                          {notice.contentSection === 'saved' || notice.contentSection === 'ready' ? (
                            <div className="public-partner-processing-steps">
                              <span>{t('PUBLIC_CATALOG_EMAIL_REGISTERED')}</span>
                              <span>{t('PUBLIC_CATALOG_NO_DUPLICATE_NOTIFICATION')}</span>
                              <span>{t('PUBLIC_CATALOG_NOTIFY_WHEN_MERGED')}</span>
                            </div>
                          ) : (
                            <Fragment>
                              <div className="public-partner-processing-steps">
                                <span>{t('PUBLIC_CATALOG_CONTENT_AVAILABLE_HINT')}</span>
                                <span>{t('PUBLIC_CATALOG_MY_VIDEO_AFTER_MERGE')}</span>
                                <span>{t('PUBLIC_CATALOG_EMAIL_WHEN_READY')}</span>
                              </div>
                              <Button
                                variant="primary"
                                className="public-partner-processing-action"
                                disabled={videoReadyNotificationLoadingId === notice.contentId}
                                onClick={() => {
                                  const item = catalog.items.find(catalogItem => (
                                    catalogItem.contentId === notice.contentId
                                    && catalogItem.contentType === notice.contentType
                                  ));
                                  subscribeVideoReadyNotification(item);
                                }}
                              >
                                {videoReadyNotificationLoadingId === notice.contentId
                                  ? t('SAVING')
                                  : t('PUBLIC_CATALOG_NOTIFY_ME')}
                              </Button>
                            </Fragment>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {notice && notice.action !== 'video_ready_notification' && notice.action !== 'video_processing_status' && (
                  <Row style={{marginTop: 20}}>
                    <Col md={12}>
                      <Alert variant={notice.variant}>{notice.message}</Alert>
                    </Col>
                  </Row>
                )}

                <Modal
                  show={Boolean(paywallOffer)}
                  onHide={() => setPaywallOffer(null)}
                  centered
                  size="lg"
                  className="public-partner-options-modal"
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      <span className="public-partner-options-kicker">ReplayerTV</span>
                      <span>
                        {paywallOffer && paywallOffer.managementMode
                          ? t('PUBLIC_CATALOG_SUBSCRIPTION_OPTIONS')
                          : paywallSpecialEventPresentation
                            ? t(paywallSpecialEventPresentation.modalTitleKey)
                            : t('PUBLIC_CATALOG_CHOOSE_VIEWING_OPTION')}
                      </span>
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p className="public-partner-options-intro">
                      {paywallOffer && paywallOffer.managementMode
                        ? t('PUBLIC_CATALOG_SUBSCRIPTION_OPTIONS_INTRO')
                        : paywallSpecialEventPresentation
                          ? t(paywallSpecialEventPresentation.introKey)
                          : t('PUBLIC_CATALOG_VIEWING_OPTIONS_INTRO')}
                    </p>
                    {paywallOffer && (
                      <Fragment>
                        {paywallOffer.managementMode && (
                          <Alert variant="info" style={{marginBottom: 16}}>
                            <strong>{t('PUBLIC_CATALOG_CURRENT_SUBSCRIPTION_VALID')}</strong>
                            <div style={{marginTop: 6}}>
                              {currentCatalogSubscriptionExpiry
                                ? t('PUBLIC_CATALOG_NEW_SUBSCRIPTION_START_DATE', {date: currentCatalogSubscriptionExpiry})
                                : t('PUBLIC_CATALOG_NEW_SUBSCRIPTION_AFTER_CURRENT')}
                            </div>
                          </Alert>
                        )}
                        {!paywallOffer.requiredAffiliation && !paywallOffer.managementMode && (
                          <div className="public-partner-options-access-check">
                            <span className="public-partner-options-access-icon" aria-hidden="true">
                              <FaUserCheck />
                            </span>
                            <span className="public-partner-options-access-copy">
                              <strong>{t('PUBLIC_CATALOG_ALREADY_HAVE_ACCESS')}</strong>
                              <span>
                                {t(paywallSpecialEventPresentation
                                  ? paywallSpecialEventPresentation.accessHelpKey
                                  : 'PUBLIC_CATALOG_ALREADY_HAVE_ACCESS_HELP')}
                              </span>
                            </span>
                            <Button
                              variant="primary"
                              className="public-partner-options-access-action"
                              disabled={playActionLoadingId === `${paywallOffer.contentType}:${paywallOffer.contentId}`}
                              onClick={verifySelectedContentAccess}
                            >
                              {playActionLoadingId === `${paywallOffer.contentType}:${paywallOffer.contentId}`
                                ? t('PUBLIC_CATALOG_VERIFYING')
                                : token
                                  ? t('PUBLIC_CATALOG_CHECK_MY_ACCESS')
                                  : t('PUBLIC_CATALOG_LOGIN_AND_WATCH')}
                            </Button>
                          </div>
                        )}

                          {paywallOffer.requiredAffiliation && (
                            <Alert variant="info" style={{marginTop: 10}}>
                              <strong>
                                {paywallOffer.requiredAffiliation.affiliationName || t('PUBLIC_CATALOG_COACH_PROFILE')}
                              </strong>
                              <div style={{marginTop: 6}}>
                                {AFFILIATION_STATUS_TRANSLATION_KEY[paywallOffer.requiredAffiliation.status]
                                  ? t(AFFILIATION_STATUS_TRANSLATION_KEY[paywallOffer.requiredAffiliation.status])
                                  : t('PUBLIC_CATALOG_CLUB_AUTH_REQUIRED')}
                              </div>
                              <div style={{marginTop: 8}}>
                                {t('PUBLIC_CATALOG_COACH_PROFILE_HELP')}
                              </div>
                              <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14}}>
                                {!token && (
                                  <Button
                                    variant="primary"
                                    onClick={() => {
                                      setPaywallOffer(null);
                                      activateAuthenticatedClubPage();
                                      redirectToLoginWithResume();
                                    }}
                                  >
                                    {t('LOGIN')}
                                  </Button>
                                )}
                                {coachAffiliation && (
                                  <Button
                                    variant="outline-primary"
                                    disabled={coachAffiliation.status !== 'available'}
                                    onClick={() => {
                                      setPaywallOffer(null);
                                      openAffiliationConfirmation(coachAffiliation);
                                    }}
                                  >
                                    {coachAffiliation.status === 'available'
                                      ? t('PUBLIC_CATALOG_REQUEST_COACH_PROFILE')
                                      : t('PUBLIC_CATALOG_COACH_PROFILE_STATUS', {status: AFFILIATION_STATUS_TRANSLATION_KEY[coachAffiliation.status] ? t(AFFILIATION_STATUS_TRANSLATION_KEY[coachAffiliation.status]) : coachAffiliation.status})}
                                  </Button>
                                )}
                              </div>
                            </Alert>
                          )}

                          {!paywallOffer.requiredAffiliation && paywallOffer.offers && (
                            <div className="public-partner-options-grid">
                              {paywallOffer.offers.subscriptions && paywallOffer.offers.subscriptions.map((sub, idx) => {
                                const specialEventPresentation = sub.kind === 'rtv_special_event'
                                  ? createPublicSpecialEventPresentation(sub, locale)
                                  : null;
                                return (
                                  <div
                                    key={`${sub.kind || 'subscription'}-${idx}`}
                                    className={`public-partner-option-card public-partner-option-card-${sub.kind || 'subscription'}`}
                                  >
                                  <div className="public-partner-option-card-accent" />
                                  {sub.kind === 'rtv_dedicated' && (
                                    <span className="public-partner-option-badge">
                                      {sub.months
                                        ? t('PUBLIC_CATALOG_MONTHS', {count: sub.months})
                                        : t('PUBLIC_CATALOG_THIS_CLUB')}
                                    </span>
                                  )}
                                  {sub.kind === 'rtv_unlimited' && (
                                    <span className="public-partner-option-badge public-partner-option-badge-unlimited">
                                      {t('PUBLIC_CATALOG_ALL_REPLAYERTV')}
                                    </span>
                                  )}
                                  <h5>
                                    {specialEventPresentation
                                      ? t(specialEventPresentation.titleKey)
                                      : SUBSCRIPTION_KIND_LABEL[sub.kind]
                                        ? t(SUBSCRIPTION_KIND_LABEL[sub.kind])
                                        : sub.kind}
                                  </h5>
                                  <p className="public-partner-option-scope">
                                    {sub.kind === 'rtv_dedicated'
                                      ? t('PUBLIC_CATALOG_DEDICATED_SCOPE', {club: catalog.partner ? catalog.partner.name : t('PUBLIC_CATALOG_THIS_CLUB')})
                                      : sub.kind === 'rtv_unlimited'
                                        ? t('PUBLIC_CATALOG_UNLIMITED_SCOPE')
                                        : t('PUBLIC_CATALOG_EVENT_SCOPE')}
                                  </p>
                                  {sub.channelName && (
                                    <div className="public-partner-option-channel">
                                      {t('CHANNEL')}:
                                      {' '}
                                      <strong>{sub.channelName}</strong>
                                    </div>
                                  )}
                                  {sub.label && (
                                    <div className="public-partner-option-channel">
                                      <strong>{sub.label}</strong>
                                      {sub.promotionEndsAt && (
                                        <span>
                                          {' '}
                                          {t('UNTIL')}
                                          {' '}
                                          {new Date(sub.promotionEndsAt).toLocaleDateString(t('LOCALE_CODE'))}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <ul className="public-partner-option-benefits">
                                    {specialEventPresentation
                                      ? specialEventPresentation.benefits.map(benefit => (
                                        <li key={benefit.key}>{t(benefit.key, benefit.values)}</li>
                                      ))
                                      : (
                                        <Fragment>
                                          <li>
                                            {sub.autoRenew === false
                                              ? t('PUBLIC_CATALOG_ENDS_AUTOMATICALLY')
                                              : t('PUBLIC_CATALOG_RENEWS_WITH_CREDIT')}
                                          </li>
                                          <li>{t('PUBLIC_CATALOG_CANCEL_ANYTIME')}</li>
                                        </Fragment>
                                      )}
                                  </ul>
                                  <div className="public-partner-option-price">
                                    <strong>{formatEur(sub.priceEur, locale)} EUR</strong>
                                    <span>
                                      {t(specialEventPresentation
                                        ? specialEventPresentation.priceSuffixKey
                                        : sub.planId ? 'TOTAL' : 'PER_MONTH')}
                                    </span>
                                  </div>
                                  <div className="public-partner-option-action">
                                    <Button
                                      variant={sub.kind === 'rtv_dedicated' ? 'primary' : 'outline-primary'}
                                      block
                                      disabled={Boolean(paywallOffer.managementMode) || purchaseActionId === `subscription:${sub.kind}:${sub.planId || sub.channelId || 'all'}`}
                                      onClick={() => purchaseSubscription(sub)}
                                    >
                                      {paywallOffer.managementMode
                                        ? currentCatalogSubscriptionExpiry
                                          ? t('PUBLIC_CATALOG_STARTS_ON', {date: currentCatalogSubscriptionExpiry})
                                          : t('PUBLIC_CATALOG_STARTS_AFTER_CURRENT')
                                        : purchaseActionId === `subscription:${sub.kind}:${sub.planId || sub.channelId || 'all'}`
                                        ? t('PUBLIC_CATALOG_ACTIVATING')
                                        : sub.kind === 'rtv_dedicated'
                                          ? sub.planId
                                            ? t('PUBLIC_CATALOG_ACTIVATE_MONTHS', {count: sub.months})
                                            : t('PUBLIC_CATALOG_SUBSCRIBE_CLUB')
                                          : sub.kind === 'rtv_unlimited'
                                            ? t('PUBLIC_CATALOG_ACTIVATE_UNLIMITED')
                                            : specialEventPresentation
                                              ? t(specialEventPresentation.actionKey)
                                              : t('PUBLIC_CATALOG_ACTIVATE_SUBSCRIPTION')}
                                    </Button>
                                  </div>
                                  </div>
                                );
                              })}

                              {paywallOffer.offers.singlePurchase && paywallOffer.offers.singlePurchase.available && (
                                <div className="public-partner-option-card public-partner-option-card-single">
                                  <div className="public-partner-option-card-accent" />
                                  <span className="public-partner-option-badge public-partner-option-badge-single">
                                    {t('PUBLIC_CATALOG_ONE_MATCH')}
                                  </span>
                                  <h5>{t('PUBLIC_CATALOG_SINGLE_PURCHASE')}</h5>
                                  <p className="public-partner-option-scope">
                                    {t('PUBLIC_CATALOG_SINGLE_PURCHASE_SCOPE')}
                                  </p>
                                  <ul className="public-partner-option-benefits">
                                    <li>{t('PUBLIC_CATALOG_ACCESS_THIS_VIDEO')}</li>
                                    <li>{t('PUBLIC_CATALOG_NO_AUTO_RENEWAL')}</li>
                                  </ul>
                                  <div className="public-partner-option-price">
                                    <strong>{formatEur(paywallOffer.offers.singlePurchase.priceEur, locale)} EUR</strong>
                                    <span>{t('PUBLIC_CATALOG_ONE_TIME')}</span>
                                  </div>
                                  <div className="public-partner-option-action">
                                    <Button
                                      variant="outline-primary"
                                      block
                                      disabled={purchaseActionId === `purchase:${paywallOffer.contentType}:${paywallOffer.contentId}`}
                                      onClick={purchaseSingleVideo}
                                    >
                                      {purchaseActionId === `purchase:${paywallOffer.contentType}:${paywallOffer.contentId}`
                                        ? t('PUBLIC_CATALOG_PURCHASING')
                                        : t('PUBLIC_CATALOG_BUY_VIDEO')}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                      </Fragment>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setPaywallOffer(null)}>
                      {t('CLOSE')}
                    </Button>
                  </Modal.Footer>
                </Modal>

                <Modal
                  show={showTopUp}
                  onHide={() => setShowTopUp(false)}
                  centered
                  className="public-partner-options-modal"
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      <span className="public-partner-options-kicker">{t('PUBLIC_CATALOG_INSUFFICIENT_CREDIT')}</span>
                      <span>{t('PUBLIC_CATALOG_TOP_UP_ACCOUNT')}</span>
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p>{t('PUBLIC_CATALOG_TOP_UP_REQUIRED')}</p>
                    <Alert variant="warning" style={{marginBottom: 0}}>
                      {t('PUBLIC_CATALOG_TOP_UP_RETURN')}
                    </Alert>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowTopUp(false)}>
                      {t('CLOSE')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        setShowTopUp(false);
                        setPaywallOffer(null);
                        navigateFromClub('/user-top-up-account');
                      }}
                    >
                      {t('PUBLIC_CATALOG_TOP_UP_ACCOUNT')}
                    </Button>
                  </Modal.Footer>
                </Modal>

                <Modal
                  show={Boolean(authenticationRequired)}
                  onHide={closeAuthenticationRequired}
                  centered
                  className="public-partner-options-modal"
                >
                  <Modal.Header closeButton>
                    <Modal.Title>{t('PUBLIC_CATALOG_LOGIN_TO_CONTINUE')}</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p>{t('PUBLIC_CATALOG_AUTH_REQUIRED')}</p>
                    <Alert variant="info" style={{marginBottom: 0}}>
                      <strong>{t('PUBLIC_CATALOG_CHOICE_SAVED')}</strong>
                      <div style={{marginTop: 6}}>
                        {t('PUBLIC_CATALOG_RETURN_AFTER_LOGIN')}
                      </div>
                    </Alert>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="outline-secondary" onClick={closeAuthenticationRequired}>
                      {t('CANCEL')}
                    </Button>
                    <Button variant="secondary" onClick={() => continueAuthentication('signup')}>
                      {t('SIGN_UP')}
                    </Button>
                    <Button variant="primary" onClick={() => continueAuthentication('login')}>
                      {t('LOGIN')}
                    </Button>
                  </Modal.Footer>
                </Modal>

                <Modal
                  show={Boolean(notice && notice.action === 'video_processing_status')}
                  onHide={() => setNotice(null)}
                  centered
                  className="public-partner-options-modal"
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      {notice && notice.title ? notice.title : t('PUBLIC_CATALOG_VIDEO_STATUS')}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p>
                      {notice ? notice.message : ''}
                    </p>
                    <Alert variant="info" style={{marginBottom: 0}}>
                      <strong>{t('PUBLIC_CATALOG_HIGHLIGHTS_AVAILABLE')}</strong>
                      <div style={{marginTop: 6}}>
                        {t('PUBLIC_CATALOG_HIGHLIGHTS_PROCESSING_HELP')}
                      </div>
                    </Alert>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="primary" onClick={() => setNotice(null)}>
                      {t('UNDERSTOOD')}
                    </Button>
                  </Modal.Footer>
                </Modal>

                <Modal
                  show={Boolean(videoUnavailableNotice)}
                  onHide={() => setVideoUnavailableNotice(null)}
                  centered
                  className="public-partner-options-modal"
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      {videoUnavailableNotice ? videoUnavailableNotice.title : t('PUBLIC_CATALOG_VIDEO_UNAVAILABLE')}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p style={{marginBottom: 0}}>
                      {videoUnavailableNotice ? videoUnavailableNotice.message : ''}
                    </p>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="primary" onClick={() => setVideoUnavailableNotice(null)}>
                      {t('UNDERSTOOD')}
                    </Button>
                  </Modal.Footer>
                </Modal>

                {skyworkViewModel.ribbon.actions.length > 0 && (
                  <PublicPartnerSkyworkActionRibbon
                    actions={skyworkViewModel.ribbon.actions}
                    ariaLabel={skyworkViewModel.ribbon.ariaLabel}
                    onAction={handleSkyworkAction}
                    sequence={skyworkViewModel.ribbon.sequence}
                  />
                )}

                {!isTvView && catalog.partner && (
                  <section
                    id="club-showcase"
                    className={`public-partner-showcase${showClubShowcase ? ' public-partner-showcase--open' : ''}`}
                    style={{scrollMarginTop: 110}}
                  >
                    {showClubShowcase && (
                      <Fragment>
                    <Row>
                      <Col lg={7} style={{marginBottom: 24}}>
                        <Card className="h-100">
                          <Card.Body>
                            <Card.Title>{catalog.partner.name}</Card.Title>
                            {catalog.partner.description && <Card.Text>{catalog.partner.description}</Card.Text>}
                            {catalog.partner.extendedDescription && <Card.Text>{catalog.partner.extendedDescription}</Card.Text>}

                            {(catalog.partner.address || catalog.partner.city) && (
                              <div style={{marginTop: 20}}>
                                <strong>{t('ADDRESS')}</strong>
                                <div>
                                  {[catalog.partner.address, catalog.partner.postalCode, catalog.partner.city, catalog.partner.province]
                                    .filter(Boolean)
                                    .join(' ')}
                                </div>
                              </div>
                            )}
                            {catalog.partner.phoneNumber && (
                              <div style={{marginTop: 12}}>
                                <strong>{t('PHONE')}: </strong>
                                <a href={`tel:${catalog.partner.phoneNumber}`}>{catalog.partner.phoneNumber}</a>
                              </div>
                            )}
                            <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 20}}>
                              {catalog.partner.website && (
                                <a
                                  href={catalog.partner.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={t('WEBSITE')}
                                  title={t('WEBSITE')}
                                  style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    border: '2px solid #d7dde5',
                                    boxShadow: '0 2px 8px rgba(0, 21, 47, 0.16)',
                                    color: '#00152f',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 14,
                                    fontWeight: 'bold',
                                    textDecoration: 'none'
                                  }}
                                >
                                  www
                                </a>
                              )}
                              {[
                                ['Facebook', catalog.partner.facebook, facebookIcon],
                                ['Instagram', catalog.partner.instagram, instagramIcon],
                                ['YouTube', catalog.partner.youtube, youtubeIcon]
                              ].filter(([, url]) => url).map(([label, url, icon]) => (
                                <a
                                  key={label}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={label}
                                  title={label}
                                  style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    border: '2px solid #d7dde5',
                                    boxShadow: '0 2px 8px rgba(0, 21, 47, 0.16)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <img src={icon} alt={label} style={{width: 30, height: 30}}/>
                                </a>
                              ))}
                            </div>
                            <ul className="public-partner-facilities">
                              {Object.entries(catalog.partner.facilities || {})
                                .filter(([, enabled]) => enabled)
                                .map(([facility]) => {
                                  const label = FACILITY_LABELS[facility] ? t(FACILITY_LABELS[facility]) : facility;
                                  const icon = FACILITY_ICONS[facility];

                                  return icon && (
                                    <li
                                      key={facility}
                                      className="public-partner-facility"
                                      tabIndex="0"
                                      aria-label={label}
                                    >
                                      <img src={icon} alt="" aria-hidden="true"/>
                                      <span>{label}</span>
                                    </li>
                                  );
                                })}
                            </ul>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col lg={5} style={{marginBottom: 24}}>
                        {catalog.partner.galleryImages && catalog.partner.galleryImages.length > 0 ? (
                          <Carousel interval={null}>
                            {catalog.partner.galleryImages.map(image => (
                              <Carousel.Item key={image}>
                                <img
                                  className="d-block w-100"
                                  src={`${Configuration.apiEndPoint}/images/${catalog.partner.id}?load=${encodeURIComponent(image)}`}
                                  alt={t('PUBLIC_CATALOG_CLUB_IMAGE_ALT', {club: catalog.partner.name})}
                                  style={{height: 320, objectFit: 'cover'}}
                                />
                              </Carousel.Item>
                            ))}
                          </Carousel>
                        ) : (
                          <Card className="h-100">
                            <Card.Body className="d-flex align-items-center justify-content-center text-muted">
                              {t('PUBLIC_CATALOG_NO_GALLERY_IMAGE')}
                            </Card.Body>
                          </Card>
                        )}
                      </Col>
                    </Row>

                    <Row>
                      {catalog.partner.openingHours && catalog.partner.openingHours.sportsCenter && (
                        <Col md={6} style={{marginBottom: 24}}>
                          <Card className="h-100">
                            <Card.Body>
                              <Card.Title>{t('PUBLIC_CATALOG_CLUB_HOURS')}</Card.Title>
                              {catalog.partner.openingHours.sportsCenter.split('\n').map((line, index) => (
                                <div key={`center-hour-${index}`}>{line}</div>
                              ))}
                            </Card.Body>
                          </Card>
                        </Col>
                      )}
                      {catalog.partner.openingHours && catalog.partner.openingHours.secretary && (
                        <Col md={6} style={{marginBottom: 24}}>
                          <Card className="h-100">
                            <Card.Body>
                              <Card.Title>{t('PUBLIC_CATALOG_OFFICE_HOURS')}</Card.Title>
                              {catalog.partner.openingHours.secretary.split('\n').map((line, index) => (
                                <div key={`secretary-hour-${index}`}>{line}</div>
                              ))}
                            </Card.Body>
                          </Card>
                        </Col>
                      )}
                    </Row>

                    <div className="public-partner-showcase-collapse">
                      <Button
                        variant="outline-primary"
                        onClick={() => setShowClubShowcase(false)}
                      >
                        {t('PUBLIC_CATALOG_COLLAPSE_SHOWCASE')}
                      </Button>
                    </div>
                    <hr/>
                      </Fragment>
                    )}
                  </section>
                )}

                {hasSkyworkEvents && (
                  <PublicPartnerSkyworkEvents
                    counts={{live: 0, replay: 0, upcoming: 0}}
                    items={specialEventCards}
                    labels={skyworkJourneyLabels.events}
                    onSelect={handleSkyworkEventSelect}
                  />
                )}

                {hasPlannerClubSection && (
                  <PublicPartnerSkyworkPlanner
                    labels={skyworkJourneyLabels.planner}
                    onToggle={() => handleSkyworkAction({action: 'planner'})}
                    open={showBookingCalendar}
                  >
                    <div id="club-booking-calendar" className="club-booking-calendar-shell" style={{scrollMarginTop: 110}}>
                      {authenticatedClubPage && bookingAccountConfirmed ? (
                        <PlayerBookingCalendar
                          enforceWalletBeforeBooking
                          resumeSlotId={pendingBookingSlotId || window.sessionStorage.getItem(CLUB_PENDING_BOOKING_SLOT_KEY)}
                          onResumeSlotHandled={() => {
                            window.sessionStorage.removeItem(CLUB_PENDING_BOOKING_SLOT_KEY);
                            setPendingBookingSlotId(null);
                          }}
                        />
                      ) : (
                        <PublicPartnerBookingCalendar
                          mode={hasBookableClubSections ? 'booking' : 'calendar'}
                          partnerSlug={partnerSlug}
                          partnerName={catalog.partner.name}
                          affiliations={affiliations}
                          authenticatedPlayer={
                            token && profile && profile.user && profile.user.role === ROLES.player
                              ? profile.user.email || `${profile.name} ${profile.surname}`
                              : null
                          }
                          onLoginRequired={requestBookingLogin}
                          onUseAuthenticatedPlayer={useAuthenticatedPlayerForBooking}
                          onVideoSlotSelected={openCalendarVideoSlot}
                        />
                      )}
                    </div>
                  </PublicPartnerSkyworkPlanner>
                )}

                {hasOpenMatchClubSection && (
                  <section className="public-partner-open-matches replayer-club-page__journey" id="club-open-matches" aria-label={skyworkJourneyLabels.openMatch.title}>
                    <SectionHeading
                      eyebrow={skyworkJourneyLabels.openMatch.eyebrow}
                      number={skyworkJourneyLabels.openMatch.number}
                      title={skyworkJourneyLabels.openMatch.title}
                      titleAccent={skyworkJourneyLabels.openMatch.titleAccent}
                    />
                    <div className="public-partner-open-matches__panel">
                      <div className="public-partner-open-matches__intro">
                        <div>
                          <p>{skyworkJourneyLabels.openMatch.intro}</p>
                        </div>
                        <Button
                          type="button"
                          className="public-partner-open-matches__create"
                          onClick={() => handleSkyworkAction({action: 'open_match'})}
                        >
                          <FaUsers aria-hidden="true"/>{skyworkJourneyLabels.openMatch.create}
                        </Button>
                      </div>
                      {loadingClubOpenMatches ? <div className="public-partner-open-matches__empty">{skyworkJourneyLabels.openMatch.loading}</div> : null}
                      {!loadingClubOpenMatches && clubOpenMatches.length === 0 ? (
                        <div className="public-partner-open-matches__empty">
                          {skyworkJourneyLabels.openMatch.empty}
                        </div>
                      ) : null}
                      {clubOpenMatches.length > 0 ? (
                        <Row>
                          {clubOpenMatches.slice(0, 6).map(openMatch => (
                            <Col md={6} lg={4} key={openMatch.id} className="mb-3">
                              <article className="public-partner-open-match-card">
                                <div className="public-partner-open-match-card__head">
                                  <span className="public-partner-open-match-card__logo">
                                    {clubLogoUrl ? <img src={clubLogoUrl} alt="" aria-hidden="true"/> : partnerInitials(catalog.partner && catalog.partner.name)}
                                  </span>
                                  <div>
                                    <span className="public-partner-open-match-card__sport">{openMatch.sport}</span>
                                    <em>{formatOpenMatchDate(openMatch.startDatetime, locale, t('PUBLIC_CATALOG_DATE_TBD'))}</em>
                                  </div>
                                </div>
                                <strong>{formatTime(openMatch.startDatetime)}-{formatTime(openMatch.endDatetime)}</strong>
                                <small>{t('PUBLIC_CATALOG_OPEN_MATCH_PLAYERS_LEVEL', {joined: openMatch.playersJoined, required: openMatch.playersRequired, min: openMatch.minLevel || '-', max: openMatch.maxLevel || '-'})}</small>
                                <Button type="button" className="public-partner-open-match-card__action" onClick={() => history.push(`/matches/${openMatch.publicId}`)}>{t('VIEW_MATCH')}</Button>
                              </article>
                            </Col>
                          ))}
                        </Row>
                      ) : null}
                    </div>
                  </section>
                )}

                <PublicPartnerSkyworkTvSection
                  activeMode={selectedMatchListMode}
                  channels={skyworkViewModel.channels}
                  empty={
                    !loadingCatalog
                    && !catalogError
                    && rtvCatalogItems.length === 0
                    && storeCatalogItems.length === 0
                    && !loadingStoreCatalog
                  }
                  error={catalogError}
                  labels={skyworkViewModel.tv}
                  loading={loadingCatalog}
                  loadingStore={loadingStoreCatalog}
                  onOpenStore={openVideoStore}
                  onOpenSubscriptionOptions={openSubscriptionOptionsModal}
                  onRetry={loadCatalog}
                  onSelectChannel={selectRtvChannel}
                  onSelectReplay={() => setMatchListMode('replay')}
                  onSelectUpcoming={() => setMatchListMode('upcoming')}
                  replayDisabled={groupedCatalogItems.replay.length === 0}
                  selectedChannelId={selectedRtvChannelId}
                  showChannelFilter={showRtvChannelFilter}
                  showSubscriptionOptions={showSubscriptionOptionsButton}
                  storeLoaded={storeCatalogLoaded}
                  upcomingDisabled={groupedCatalogItems.upcoming.length === 0}
                >
                  {replayerTvContent}
                </PublicPartnerSkyworkTvSection>

                {!isTvView && catalog.partner && showAffiliationsClubSection && (
                  <PublicPartnerSkyworkAffiliations
                    items={skyworkAffiliations}
                    labels={skyworkAffiliationLabels}
                    loading={loadingAffiliations}
                    onRequest={affiliationId => {
                      const affiliation = affiliations.find(item => item.affiliationId === affiliationId);
                      if (affiliation) openAffiliationConfirmation(affiliation);
                    }}
                  />
                )}

                {!isTvView && catalog.partner && catalog.partner.shopActive && (
                  <PublicPartnerSkyworkShop
                    labels={skyworkJourneyLabels.shop}
                    onOpen={() => handleSkyworkAction({action: 'shop'})}
                  />
                )}

                <Modal
                  show={Boolean(affiliationToConfirm)}
                  onHide={() => {
                    setAffiliationToConfirm(null);
                    setAffiliationRequestError(null);
                  }}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      {t(affiliationToConfirm && affiliationToConfirm.renewalAllowed
                        ? 'RENEW_AFFILIATION'
                        : 'PUBLIC_CATALOG_AFFILIATION_REQUEST')}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p>
                      <strong>
                        {t('PUBLIC_CATALOG_PROFILE')}: {getAffiliationProfileName(affiliationToConfirm)}
                      </strong>
                    </p>
                    <p>{affiliationConfirmationDescription(affiliationToConfirm)}</p>
                    {!token ? (
                      <Alert variant="info">
                        {t('PUBLIC_CATALOG_AFFILIATION_AUTH_REQUIRED')}
                      </Alert>
                    ) : (
                      <Fragment>
                        {affiliationToConfirm && affiliationToConfirm.onlinePayment && (
                          <Alert variant="info">
                            {t('PUBLIC_CATALOG_AFFILIATION_PAYMENT', {price: `${formatEur(affiliationToConfirm.priceEur, locale)} EUR`})}
                          </Alert>
                        )}
                        <p>{t(affiliationToConfirm && affiliationToConfirm.renewalAllowed
                          ? 'PUBLIC_CATALOG_AFFILIATION_RENEWAL_SEND_HELP'
                          : 'PUBLIC_CATALOG_AFFILIATION_SEND_HELP')}</p>
                        <p>{t('PUBLIC_CATALOG_AFFILIATION_EMAIL_HELP')}</p>
                        {affiliationRequestError && (
                          <Alert variant="warning">
                            <strong>{affiliationRequestError}</strong>
                            {affiliationRequestError === t('PUBLIC_CATALOG_AFFILIATION_CREDIT_ERROR') && (
                              <div style={{marginTop: 12}}>
                                <Button
                                  variant="primary"
                                  onClick={() => navigateFromClub('/user-top-up-account')}
                                >
                                  {t('PUBLIC_CATALOG_TOP_UP_ACCOUNT')}
                                </Button>
                              </div>
                            )}
                          </Alert>
                        )}
                      </Fragment>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      variant="outline-danger"
                      onClick={() => {
                        setAffiliationToConfirm(null);
                        setAffiliationRequestError(null);
                      }}
                    >
                      {t('CANCEL')}
                    </Button>
                    {!token ? (
                      <Fragment>
                        <Button variant="secondary" onClick={redirectToSignUpWithResume}>
                          {t('SIGN_UP')}
                        </Button>
                        <Button variant="primary" onClick={() => redirectToLoginWithResume()}>
                          {t('LOGIN')}
                        </Button>
                      </Fragment>
                    ) : (
                      <Fragment>
                        <Button
                          variant="secondary"
                          disabled={Boolean(requestingAffiliationId)}
                          onClick={() => confirmAffiliationRequest(false)}
                        >
                          {t(requestingAffiliationId ? 'PUBLIC_CATALOG_SENDING' : 'PUBLIC_CATALOG_SEND_TO_CLUB')}
                        </Button>
                        {affiliationToConfirm && affiliationToConfirm.onlinePayment && (
                          <Button
                            variant="primary"
                            disabled={Boolean(requestingAffiliationId)}
                            onClick={() => confirmAffiliationRequest(true)}
                          >
                            {requestingAffiliationId
                              ? t('PUBLIC_CATALOG_PAYING')
                              : t('PUBLIC_CATALOG_PAY_AMOUNT', {price: `${formatEur(affiliationToConfirm.priceEur, locale)} EUR`})}
                          </Button>
                        )}
                      </Fragment>
                    )}
                  </Modal.Footer>
                </Modal>

                {!isTvView && (
                <Modal
                  show={showBookingDestinationChoice}
                  onHide={() => setShowBookingDestinationChoice(false)}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>{t('PUBLIC_CATALOG_BOOKING_CONFIRMED')}</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {t('PUBLIC_CATALOG_BOOKING_DESTINATION')}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        setShowBookingDestinationChoice(false);
                        activateAuthenticatedClubPage();
                      }}
                    >
                      {t('PUBLIC_CATALOG_STAY_CLUB')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        setShowBookingDestinationChoice(false);
                        clearClubMode();
                        history.push('/');
                      }}
                    >
                      {t('PUBLIC_CATALOG_GO_TO_REPLAYER')}
                    </Button>
                  </Modal.Footer>
                </Modal>
                )}

              </div>
            </div>
        </Container>
      </Fade>
      {
        selectedPermanentVideo === null ?
          null :
          <PlayerHighLight video={selectedPermanentVideo}/>
      }
    </Fragment>
  );
};

PublicPartnerCatalog.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      partnerSlug: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  location: PropTypes.shape({
    hash: PropTypes.string,
    search: PropTypes.string
  }).isRequired,
  profile: PropTypes.object,
  partners: PropTypes.array,
  selectedPermanentVideo: PropTypes.object,
  subscriptions: PropTypes.object,
  t: PropTypes.func.isRequired,
  token: PropTypes.string
};

PublicPartnerCatalog.defaultProps = {
  profile: null,
  partners: [],
  selectedPermanentVideo: null,
  subscriptions: null,
  token: null
};

export default withTranslation()(connect(
  ({login: {profile, token}, player: {partners, selectedPermanentVideo, subscriptions}}) => ({
    profile,
    partners,
    selectedPermanentVideo,
    subscriptions,
    token
  }),
  null
)(PublicPartnerCatalog));
